<template>
  <pre
    v-show="isDev"
    class="text-xs text-white whitespace-pre-wrap wrap-break-word"
    v-html="
      formatJSONWithLinks(
        buildInputJsonPreview(form, getModelFields(form.model))
      )
    "
  ></pre>
  <!-- 右侧配置结果区域 -->
  <div
    ref="panelGridRef"
    class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch min-h-0 lg:h-[calc(100vh-92px)] mt-3"
  >
    <!-- #region 左卡片 配置区域-->
    <UCard
      :ui="{
        header: 'h-auto border-0',
        body: 'flex flex-col h-full min-h-0 overflow-hidden border-0 !py-1',
        footer: 'h-auto mt-auto',
      }"
      class="min-w-0 min-h-0 col-span-1 lg:col-span-1 w-full h-full flex flex-col"
      :style="panelCardStyle"
    >
      <!-- 头部样式 -->
      <template #header>
        <div
          class="flex items-center justify-between flex-wrap gap-x-4 gap-y-2"
        >
          <div
            class="text-base font-semibold flex items-center h-10 whitespace-nowrap"
          >
            {{ toolTitle }}
          </div>

          <div
            class="flex items-center min-w-fit"
            v-if="modelTitles.length > 1"
          >
            <UFormField class="flex items-center h-10 whitespace-nowrap">
              <USelectMenu
                v-model="currentModelTitle"
                :items="modelTitles"
                :disabled="isWorking"
                value-key="key"
                option-attribute="label"
                :avatar="selectedModel(form.model)?.avatar"
                :ui="{
                  base: 'relative group rounded-md inline-flex items-center justify-end focus:outline-none disabled:cursor-not-allowed disabled:opacity-75 transition-colors z-50!',
                  value: 'truncate pointer-events-none text-right',
                  placeholder: 'truncate text-dimmed text-right',
                  content:
                    'max-h-60 min-w-[192px] bg-default shadow-lg rounded-md ring ring-default overflow-hidden data-[state=open]:animate-[scale-in_100ms_ease-out] data-[state=closed]:animate-[scale-out_100ms_ease-in] origin-(--reka-combobox-content-transform-origin) pointer-events-auto flex flex-col',
                  leadingAvatar: 'rounded-none bg-transparent',
                  itemLeadingAvatar: 'rounded-none bg-transparent',
                }"
                @change="onModelChange"
              >
              </USelectMenu>
            </UFormField>
          </div>
        </div>
      </template>

      <!-- 输入配置区域 -->
      <div class="flex-1 overflow-y-auto last:mb-0 custom-scroll px-1">
        <!-- 切换模型 -->
        <UFormField label="" class="mb-6" v-if="modelConfig.length > 1">
          <UTabs
            v-model="form.model"
            :items="modelConfig"
            :content="false"
            size="sm"
            color="primary"
            variant="pill"
            class="w-auto"
            value-key="key"
            :ui="{
              trigger:
                'group inline-flex items-center gap-1.5 transition-colors data-[state=active]:text-white',
              leadingAvatar: 'hidden',
            }"
          />
        </UFormField>

        <!-- 新的配置区域 -->
        <div
          v-for="[fieldKey, fieldDef] in visibleEntries"
          :key="fieldKey"
          class="mb-6"
        >
          <!-- textarea -->
          <UFormField
            class="mb-6"
            v-if="fieldDef.ui.component === 'textarea'"
            :ui="{ label: 'after:content-none' }"
          >
            <template #label>
              <TipLabel
                v-if="fieldDef.ui.tip"
                :tip="fieldDef.ui.tip"
                :required="isRequired(fieldKey)"
              >
                {{ fieldDef.ui.label }}
              </TipLabel>
              <span v-else>
                {{ fieldDef.ui.label }}
                <span v-if="isRequired(fieldKey)" class="text-error">*</span>
              </span>
            </template>
            <div class="space-y-2">
              <div
                class="border border-primary rounded-xl p-2 bg-transparent flex flex-col"
              >
                <UTextarea
                  v-model="form[fieldKey]"
                  :rows="4"
                  :placeholder="
                    fieldDef.ui.placeholder || 'Enter your prompt here'
                  "
                  :disabled="isWorking || fieldDef.disabled"
                  class="w-full border-none focus:ring-0 focus:border-none shadow-none bg-transparent resize-none custom-scroll"
                  style="box-shadow: none"
                />

                <div
                  class="flex items-center justify-end text-xs text-muted mt-2"
                >
                  <span
                    :class="
                      fieldDef.validate?.max &&
                      (form[fieldKey] || '').length > fieldDef.validate?.max
                        ? 'text-error'
                        : 'text-muted'
                    "
                    >{{ (form[fieldKey] || "").length }} /
                    {{ fieldDef.validate?.max ?? "-" }}</span
                  >
                  <UButton
                    icon="i-lucide-trash-2"
                    color="neutral"
                    variant="ghost"
                    size="xs"
                    class="ml-2"
                    :disabled="isWorking || !form[fieldKey]"
                    @click="form[fieldKey] = ''"
                    aria-label="clear"
                  />
                </div>
              </div>
            </div>
          </UFormField>

          <!-- select -->
          <div
            v-else-if="fieldDef.ui.component === 'select'"
            class="w-full flex items-center justify-between"
          >
            <label class="text-sm font-medium text-white">
              <TipLabel v-if="fieldDef.ui.tip" :tip="fieldDef.ui.tip">
                {{ fieldDef.ui.label }}
              </TipLabel>
              <span v-else>{{ fieldDef.ui.label }}</span>
            </label>
            <USelectMenu
              v-model="form[fieldKey]"
              :items="selectItems(fieldKey, fieldDef)"
              value-key="value"
              :search-input="false"
              variant="none"
              :disabled="
                isWorking ||
                singleOption(fieldKey, fieldDef) ||
                fieldDef.disabled
              "
              :ui="{
                base: 'relative group rounded-md inline-flex items-center justify-end focus:outline-none disabled:cursor-not-allowed disabled:opacity-75 transition-colors',
                value: 'truncate pointer-events-none text-right',
                placeholder: 'truncate text-dimmed text-right',
                content:
                  'max-h-60 min-w-[192px] bg-default shadow-lg rounded-md ring ring-default overflow-hidden data-[state=open]:animate-[scale-in_100ms_ease-out] data-[state=closed]:animate-[scale-out_100ms_ease-in] origin-(--reka-combobox-content-transform-origin) pointer-events-auto flex flex-col',
              }"
            />
          </div>

          <!-- ratioSelect：始终显示，和裁切弹窗比例解耦 -->
          <div
            v-else-if="fieldDef.ui.component === 'ratioSelect'"
            class="w-full flex items-center justify-between"
          >
            <label class="text-sm font-medium text-white">
              <TipLabel v-if="fieldDef.ui.tip" :tip="fieldDef.ui.tip">
                {{ fieldDef.ui.label }}
              </TipLabel>
              <span v-else>{{ fieldDef.ui.label }}</span>
            </label>
            <USelectMenu
              v-model="form[fieldKey]"
              :items="ratioItems(fieldKey, fieldDef)"
              value-key="value"
              :icon="ratioIcon(fieldKey, fieldDef)"
              variant="none"
              :disabled="isWorking || fieldDef.disabled"
              :ui="{
                base: 'relative group rounded-md inline-flex items-center justify-end focus:outline-none disabled:cursor-not-allowed disabled:opacity-75 transition-colors',
                value: 'truncate pointer-events-none text-right',
                placeholder: 'truncate text-dimmed text-right',
                content:
                  'max-h-60 min-w-[192px] bg-default shadow-lg rounded-md ring ring-default overflow-hidden data-[state=open]:animate-[scale-in_100ms_ease-out] data-[state=closed]:animate-[scale-out_100ms_ease-in] origin-(--reka-combobox-content-transform-origin) pointer-events-auto flex flex-col',
              }"
            />
          </div>

          <!-- switch -->
          <div
            v-else-if="fieldDef.ui.component === 'switch'"
            class="w-full flex items-center justify-between"
          >
            <label
              class="text-sm font-medium text-white flex items-center gap-2 min-w-0"
            >
              <TipLabel v-if="fieldDef.ui.tip" :tip="fieldDef.ui.tip">
                {{ fieldDef.ui.label }}
              </TipLabel>
              <span v-else>{{ fieldDef.ui.label }}</span>
              <span
                v-if="isMergeBgmField(fieldKey)"
                class="inline-flex items-center gap-1 shrink-0"
              >
                <UButton
                  :icon="mergeBgmIsPlaying ? 'i-lucide-pause' : 'i-lucide-play'"
                  color="neutral"
                  variant="ghost"
                  size="xs"
                  :disabled="!mergeBgmAudioSrc"
                  :loading="mergeBgmIsLoading"
                  title="Preview BGM"
                  :aria-label="mergeBgmIsPlaying ? 'Pause BGM' : 'Preview BGM'"
                  @click.stop.prevent="toggleMergeBgmPreview"
                />
                <span
                  v-if="mergeBgmShowVisualizer"
                  class="merge-bgm-visualizer flex items-end gap-0.5 h-3.5"
                  aria-hidden="true"
                >
                  <span class="merge-bgm-bar" />
                  <span class="merge-bgm-bar merge-bgm-bar--delay-1" />
                  <span class="merge-bgm-bar merge-bgm-bar--delay-2" />
                  <span class="merge-bgm-bar merge-bgm-bar--delay-3" />
                </span>
              </span>
            </label>
            <USwitch
              v-model="form[fieldKey]"
              :disabled="isWorking || fieldDef.disabled"
            />
          </div>

          <!-- uploader（图片/视频/音频都走它，靠 validate.formats 区分） -->
          <UFormField
            v-else-if="fieldDef.ui.component === 'uploader'"
            :ui="{ label: 'after:content-none' }"
            :hint="
              fieldDef.validate?.max > 1
                ? `${(form[fieldKey] || []).length}/${fieldDef.validate?.max}`
                : ''
            "
          >
            <template #label>
              <TipLabel
                v-if="fieldDef.ui.tip"
                :tip="fieldDef.ui.tip"
                :required="isRequired(fieldKey)"
              >
                {{ fieldDef.ui.label }}
              </TipLabel>
              <span v-else>
                {{ fieldDef.ui.label }}
                <span v-if="isRequired(fieldKey)" class="text-error">*</span>
              </span>
            </template>
            <MediaUploader
              v-model="form[fieldKey]"
              :media-type="detectMediaType(fieldDef)"
              :max-files="fieldDef.validate?.max ?? 1"
              :formats="fieldDef.validate?.formats ?? []"
              :disabled="isWorking || fieldDef.disabled"
              :loading="isLoadingInputImages"
              :enable-cropper="enableUploaderCropper"
            />
          </UFormField>

          <!-- input-scrollbar（你的宽高那套：UInput + USlider） -->
          <div
            v-else-if="fieldDef.ui.component === 'input-scrollbar'"
            class="mb-6 w-full flex flex-col gap-3"
          >
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-white">
                <TipLabel v-if="fieldDef.ui.tip" :tip="fieldDef.ui.tip">
                  {{ fieldDef.ui.label }}
                </TipLabel>
                <span v-else>{{ fieldDef.ui.label }}</span>
              </label>
              <div class="flex items-center gap-1">
                <UInput
                  v-model="form[fieldKey]"
                  type="text"
                  size="xs"
                  class="w-16"
                  :disabled="isWorking || fieldDef.disabled"
                  variant="outline"
                  :ui="{ base: 'text-center' }"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  @blur="
                    form[fieldKey] = handleLimit(
                      fieldDef.validate?.min,
                      fieldDef.validate?.max,
                      form[fieldKey]
                    )
                  "
                />
                <span v-if="fieldDef.ui.unit" class="text-xs text-gray-400">
                  {{ fieldDef.ui.unit }}
                </span>
              </div>
            </div>
            <USlider
              v-model="form[fieldKey]"
              :min="fieldDef.validate?.min"
              :max="fieldDef.validate?.max"
              :disabled="isWorking || fieldDef.disabled"
            />
          </div>

          <!-- fallback -->
          <div v-else class="text-xs text-gray-400">
            Unknown component: {{ fieldDef.ui.component }} ({{ fieldKey }})
          </div>
        </div>

        <!-- 是否公开 -->
        <div class="mb-6 w-full flex items-center justify-between">
          <div class="flex text-sm items-center">
            <span class="text-sm font-medium text-white"
              >Public Visibility</span
            >
            <UPopover mode="hover" :open-delay="120">
              <UIcon
                name="material-symbols-light:info-outline-rounded"
                class="ml-1 text-gray-500"
                style="font-size: 1.1em"
              />
              <template #content>
                <div class="p-2 text-xs max-w-xs">
                  When this option is enabled, your creations may be chosen by
                  SharkFoto to be featured in the Community.
                </div>
              </template>
            </UPopover>
          </div>
          <USwitch v-model="form.isPublic" :disabled="isWorking" />
        </div>
      </div>

      <!-- 底部固定区域 -->
      <template #footer>
        <div class="space-y-4">
          <!-- 生成按钮或取消按钮 -->
          <UButton
            v-if="!isWorking || isApiRequestSent"
            class="w-full text-center justify-center"
            size="lg"
            icon="material-symbols-light:star-shine-outline-rounded"
            :loading="isWorking || isLoadingInputImages"
            :disabled="isGenerateDisabled"
            @click="onGenerate(form)"
          >
            {{ sampleConfig?.generateBtnLabel || "Generate" }}</UButton
          >
          <UButton
            v-else
            class="w-full text-center justify-center"
            size="lg"
            icon="material-symbols-light:cancel-outline-rounded"
            color="error"
            variant="outline"
            :loading="isCancelling"
            @click="onCancelGenerate"
          >
            Cancel
          </UButton>
          <ClientOnly>
            <div
              class="flex items-center justify-center gap-2 text-xs text-muted"
              v-if="token"
            >
              <UIcon
                name="material-symbols-light:diamond-shine-outline-rounded"
                class="p-0 size-4"
              />
              <span
                >Cost: {{ selectedCredit() }}
                {{ isGenerateDisabled ? "+" : "" }} Credit(s)</span
              >
            </div>
            <div
              v-else
              class="flex items-center justify-center gap-2 text-xs text-muted"
            >
              <span>Generated by AI. Please use responsibly.</span>
            </div></ClientOnly
          >
        </div>
      </template>
    </UCard>
    <!-- #endregion -->

    <!-- #region 右卡片 任务列表区域-->
    <UCard
      class="flex flex-col min-h-0 col-span-1 lg:col-span-2 h-full"
      :style="panelCardStyle"
      id="tasks-card"
      :ui="{
        header: 'border-0',
        body: 'h-full min-h-0 overflow-y-auto py-1 sm:py-1 custom-scroll',
        footer: 'py-0 h-auto',
      }"
    >
      <template #header>
        <div
          class="flex flex-col sm:flex-row sm:items-start sm:justify-between w-full"
        >
          <div class="flex items-center shrink-0">
            <UTabs
              :items="cardTitleItems"
              v-model="cardTitleTab"
              color="primary"
              variant="link"
              :ui="{
                label: 'text-left! text-base font-semibold',
                trigger:
                  'group px-0 inline-flex items-center gap-1.5 transition-colors data-[state=active]:text-white py-0',
                indicator:
                  cardTitleItems.length == 1
                    ? 'hidden gap-3 px-0'
                    : 'gap-3 px-0',
                list:
                  cardTitleItems.length == 1
                    ? 'border-0 gap-3 px-0'
                    : 'border-b-0 gap-3 px-0',
              }"
            />
          </div>

          <div
            v-if="showTaskActions"
            class="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end text-sm text-gray-500 mt-1 sm:mt-0 sm:ml-4 min-w-0"
          >
            <span
              class="block sm:grow sm:truncate text-left"
              :title="'Latest 10 unarchived tasks. See all in My Tasks.'"
            >
              Latest 10 unarchived tasks. See all in My Tasks.
            </span>

            <div
              class="flex items-center gap-2 mt-1 sm:mt-0 sm:ml-4 shrink-0 justify-start sm:justify-end"
            >
              <UButton
                v-if="hasPendingTasks"
                icon="material-symbols-light:sync-rounded"
                size="sm"
                variant="ghost"
                color="neutral"
                :loading="isRefreshingAll"
                @click="refreshAllPendingTasks()"
                title="Refreshing All in the Predicting"
              />
              <UButton
                icon="material-symbols-light:directory-sync-rounded"
                size="sm"
                color="neutral"
                variant="ghost"
                @click="loadUserTasks((networkRequest = true))"
                title="Load User Tasks"
              />
              <UButton
                icon="material-symbols-light:folder-check-rounded"
                size="sm"
                variant="ghost"
                color="neutral"
                to="/my/tasks"
                title="View All Tasks"
                target="_blank"
              />
            </div>
          </div>
        </div>
      </template>

      <div class="flex flex-col gap-4 h-full">
        <!-- 加载历史记录状态 -->
        <div
          v-if="cardTitleTab === 'tasks' && isLoadingHistory"
          class="text-center text-muted w-full flex flex-col justify-center"
          :class="imgRatio"
        >
          <!-- 加载状态 -->
          <div class="flex justify-center items-center py-20">
            <div
              class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"
            ></div>
          </div>
        </div>

        <!-- 空状态 -->
        <div
          v-else-if="showTaskEmptyState"
          class="flex w-full h-full items-center justify-center"
        >
          <div class="text-center text-muted px-6 flex flex-col items-center">
            <!-- 交错大图标组合 -->
            <div
              class="relative mb-5 w-30 h-30 flex items-center justify-center"
            >
              <!-- Icon 1 左上角 -->
              <span
                class="absolute left-15 top-2 text-4xl opacity-80 rotate-[-10deg] text-purple-300"
              >
                <UIcon name="material-symbols-light:queue-music-rounded" />
              </span>
              <!-- Icon 2 右下角 层级高一些 -->
              <span
                class="absolute right-0 bottom-1 text-5xl opacity-90 z-10 rotate-13 text-blue-300"
              >
                <UIcon name="material-symbols-light:photo-prints-outline" />
              </span>
              <!-- Icon 3 居中偏下, 层级在最上 -->
              <span
                class="absolute left-4 top-18 text-3xl opacity-90 z-20 rotate-[-5deg] text-fuchsia-400"
              >
                <UIcon name="material-symbols-light:photo-library-rounded" />
              </span>
            </div>
            <p class="text-base text-white/80">Nothing to see here... yet!</p>
            <p class="mt-2 text-sm text-white/45">
              Ready to make something amazing? Give it a try.
            </p>
          </div>
        </div>

        <div v-show="cardTitleTab === 'effects'" class="w-full h-auto">
          <EffectRecommendationsPanel :refresh-key="effectRecommendationKey" />
        </div>

        <template
          v-if="
            cardTitleTab === 'tasks' && !isLoadingHistory && hasDisplayableTasks
          "
        >
          <TransitionGroup name="task-fade" tag="div" class="relative">
            <UCard
              v-for="task in displayedTasks"
              :key="task.id"
              :data-task-id="task.id"
              class="flex flex-col mb-4"
              :class="task.id === activeTaskId ? 'ring-1 ring-primary' : ''"
              :ui="{
                header: isTaskCollapsed(task.id)
                  ? 'p-2 border-0 select-none !px-4'
                  : 'p-2 select-none !px-4',
                body: 'h-full !p-0 border-0',
                footer: isTaskCollapsed(task.id)
                  ? 'py-0 h-auto sm:px-4'
                  : 'py-2 h-auto sm:px-4',
              }"
              @click="setActive(task.id)"
            >
              <template #header>
                <div
                  class="flex items-center justify-between"
                  @click.stop="toggleTaskCollapse(task.id)"
                >
                  <!-- 左侧：缩略图（折叠时显示） -->
                  <Transition name="expand">
                    <div
                      v-if="isTaskCollapsed(task.id) && task.outPutImageUrl"
                      class="shrink-0 justify-center items-center mr-3"
                    >
                      <div
                        class="relative w-12 h-12 rounded overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm bg-gray-100 dark:bg-gray-800"
                        @click.stop="toggleTaskCollapse(task.id)"
                      >
                        <NuxtImg
                          v-if="isShowImage || isShowImageComparator"
                          :src="task.outPutImageUrl"
                          alt="缩略图"
                          class="w-full h-full object-cover"
                          :loading="'lazy'"
                          :custom="true"
                          v-slot="{ src, isLoaded, imgAttrs }"
                          :id="`task-${task.id}-image`"
                        >
                          <img
                            v-if="isLoaded"
                            v-bind="imgAttrs"
                            :src="src"
                            class="w-full h-full object-cover transition-opacity duration-300"
                          />
                          <div
                            v-else
                            class="absolute inset-0 flex justify-center items-center h-full w-full"
                          >
                            <UIcon
                              name="i-lucide-loader-2"
                              class="w-4 h-4 animate-spin text-gray-400"
                            />
                          </div>
                        </NuxtImg>
                        <video
                          v-else-if="isShowVideo && task.outPutImageUrl"
                          :src="task.outPutImageUrl"
                          class="w-full h-full object-cover"
                          muted
                          playsinline
                          disablePictureInPicture
                          preload="metadata"
                        ></video>
                        <div
                          v-else-if="isShowRecord && task.outPutImageUrl"
                          class="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800"
                        >
                          <UIcon
                            name="i-lucide-music"
                            class="w-6 h-6 text-gray-400"
                          />
                        </div>
                        <div
                          v-else
                          class="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800"
                        >
                          <UIcon
                            name="i-lucide-image"
                            class="w-6 h-6 text-gray-400"
                          />
                        </div>
                      </div></div
                  ></Transition>

                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2 flex-wrap">
                      <!-- <div class="w-2 h-2 rounded-full bg-green-500"></div>
                      <span class="text-green-400 text-sm font-medium">{{
                        toolTitle
                      }}</span> -->
                      <span
                        v-if="task.model"
                        class="text-sm text-white mr-2 flex items-center"
                      >
                        <img
                          v-if="selectedModel(task.model)?.avatar"
                          :src="selectedModel(task.model)?.avatar.src"
                          alt="model icon"
                          class="inline-block align-middle w-4 h-4 mr-1"
                        />
                        {{ selectedModel(task.model)?.title }}
                      </span>
                      <span class="text-xs text-gray-500 mr-2">{{
                        formatTime(task.createdAt)
                      }}</span>
                      <!-- <span
                        v-if="task.ratio"
                        class="text-xs text-gray-500 mr-2 flex items-center"
                      >
                        <UIcon
                          :name="selectedRatio(task)?.icon"
                          class="mr-1"
                          style="opacity: 0.5"
                        />
                        {{ selectedRatio(task)?.label }}
                      </span> -->
                      <span
                        v-if="task.isPublic"
                        class="text-xs text-gray-500 flex items-center"
                      >
                        <UIcon
                          name="i-lucide-eye"
                          class="mr-1"
                          style="opacity: 0.5"
                        />
                        Public
                      </span>
                      <span
                        v-if="task.creditCost > 0"
                        class="text-xs text-gray-400 flex items-center mr-2"
                      >
                        <UIcon
                          name="material-symbols-light:diamond-shine-outline-rounded"
                          class="mr-1"
                          style="opacity: 0.5"
                        />
                        {{ task.creditCost }}
                      </span>
                      <pre
                        v-show="isDev"
                        class="text-xs text-white whitespace-pre-wrap wrap-break-word"
                        v-html="JSON.stringify(task, null, 2)"
                        @click.stop
                      ></pre>
                    </div>
                    <div
                      class="mt-1 flex items-center gap-1 text-sm text-muted min-w-0"
                    >
                      <!-- 图片预览图标 :open="isDev ? ($event) => (isDev = $event) : undefined"-->
                      <UPopover
                        mode="hover"
                        arrow
                        :open-delay="150"
                        :close-delay="150"
                        v-if="task?.inPutImageUrls?.length > 0"
                      >
                        <div
                          class="cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          <div
                            class="relative w-6 h-6 rounded overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm bg-gray-100 dark:bg-gray-800"
                          >
                            <NuxtImg
                              :src="task.inPutImageUrls[0]"
                              alt="预览缩略图"
                              class="w-full h-full object-cover"
                              :loading="'lazy'"
                              :custom="true"
                              v-slot="{ src, isLoaded, imgAttrs }"
                            >
                              <img
                                v-if="isLoaded"
                                v-bind="imgAttrs"
                                :src="src"
                                class="w-full h-full object-cover transition-opacity duration-300"
                              />
                              <div
                                v-else
                                class="absolute inset-0 flex justify-center items-center h-full w-full"
                              >
                                <UIcon
                                  name="i-lucide-loader-2"
                                  class="w-3 h-3 animate-spin text-gray-400"
                                />
                              </div>
                            </NuxtImg>
                          </div>
                        </div>

                        <template #content>
                          <div class="p-2">
                            <div
                              class="flex flex-col gap-4 overflow-y-auto max-h-80 min-h-20 w-64 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 items-center custom-scroll"
                            >
                              <div
                                v-for="(url, index) in task?.inPutImageUrls"
                                :key="`input-image-${task.id}-${index}`"
                                class="relative"
                              >
                                <img
                                  :ref="
                                    (el) => {
                                      if (el) {
                                        // 检查是否已有缓存的 blob URL
                                        if (imageBlobCache.has(url)) {
                                          el.src = imageBlobCache.get(url);
                                          imageLoadedStates[url] = true;
                                        } else {
                                          // 检查当前元素是否已加载
                                          nextTick(() => {
                                            checkImageCache(url, el);
                                          });
                                        }
                                      }
                                    }
                                  "
                                  :src="
                                    imageBlobCache.has(url)
                                      ? imageBlobCache.get(url)
                                      : url
                                  "
                                  :alt="`预览 ${index + 1}`"
                                  class="w-auto h-auto rounded-md shadow-md object-contain cursor-zoom-in"
                                  :class="{
                                    'opacity-0': !imageLoadedStates[url],
                                  }"
                                  @load="handleImageLoad(url, $event)"
                                  @error="handleImageLoad(url, $event)"
                                  @click="openImagePreview(url)"
                                />
                                <div
                                  v-show="!imageLoadedStates[url]"
                                  class="absolute inset-0 flex justify-center items-center pointer-events-none"
                                >
                                  <div
                                    class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"
                                  ></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>
                      </UPopover>

                      <!-- 视频预览图标 -->
                      <div
                        v-for="videoFile in task?.videoFiles"
                        :key="videoFile"
                      >
                        <UPopover
                          mode="hover"
                          arrow
                          :open-delay="150"
                          :close-delay="150"
                          v-if="videoFile"
                          :open="
                            isDev ? ($event) => (isDev = $event) : undefined
                          "
                        >
                          <div
                            class="cursor-pointer hover:opacity-80 transition-opacity relative group"
                            data-loaded="false"
                          >
                            <div
                              class="relative w-6 h-6 rounded overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm bg-gray-100 dark:bg-gray-800 flex items-center justify-center"
                            >
                              <UIcon
                                name="i-lucide-loader-2"
                                class="absolute w-3 h-3 animate-spin text-gray-400 group-data-[loaded=true]:hidden"
                              />

                              <video
                                :src="videoFile"
                                class="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300 z-10 group-data-[loaded=true]:opacity-100"
                                muted
                                playsinline
                                disablePictureInPicture
                                preload="metadata"
                                :key="`video-${videoFile}`"
                                @loadeddata="
                                  $event.target.parentElement.parentElement.dataset.loaded =
                                    'true'
                                "
                              ></video>
                              <div
                                class="absolute inset-0 items-center justify-center bg-black/10 z-20 pointer-events-none hidden group-data-[loaded=true]:flex"
                              >
                                <UIcon
                                  name="i-lucide-play"
                                  class="w-2.5 h-2.5 text-white drop-shadow-sm"
                                />
                              </div>
                            </div>
                          </div>

                          <template #content>
                            <div class="p-2">
                              <div
                                class="flex flex-col gap-2 overflow-y-auto h-80 w-64 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
                              >
                                <video
                                  class="object-contain w-full h-full"
                                  autoplay
                                  muted
                                  playsinline
                                  controls
                                  :key="`video-${videoFile}`"
                                  @loadstart="isVideoLoading = true"
                                  @canplay="isVideoLoading = false"
                                  @error="isVideoLoading = false"
                                  @click="openImagePreview(videoFile)"
                                >
                                  <source :src="videoFile" type="video/mp4" />
                                </video>
                              </div>
                            </div>
                          </template>
                        </UPopover>
                      </div>
                      <span
                        class="truncate"
                        v-if="task.prompt"
                        :title="task.prompt"
                        >{{ task.prompt }}</span
                      >
                      <UButton
                        v-if="task.prompt"
                        variant="link"
                        color="neutral"
                        size="xs"
                        icon="i-lucide-copy"
                        aria-label="Copy prompt"
                        @click.stop="
                          copyPrompt(
                            task.lyrics
                              ? `Prompt:\n${task.prompt}\n\nLyrics:\n${task.lyrics}`
                              : task.prompt
                          )
                        "
                        class="cursor-pointer"
                      />
                    </div>
                  </div>

                  <div class="flex items-center gap-1">
                    <!-- 刷新按钮（仅在预测中状态显示） -->
                    <UButton
                      v-if="isTaskPending(task)"
                      icon="material-symbols-light:sync-rounded"
                      color="primary"
                      variant="ghost"
                      :loading="refreshingTaskId === task.id || isRefreshingAll"
                      aria-label="Refresh"
                      title="Refresh the task status"
                      @click.stop="refreshSingleTask(task.id)"
                    />
                    <UPopover v-else v-model:open="archivePopoverOpen[task.id]">
                      <UButton
                        icon="majesticons:archive-line"
                        color="neutral"
                        variant="ghost"
                        :disabled="
                          isTaskPending(task) ||
                          (task.id !== activeTaskId &&
                            !getTaskOutputUrls(task).length)
                        "
                        aria-label="Archive"
                        title="Archive"
                        @click.stop
                      />

                      <template #content>
                        <div
                          class="p-4 space-y-3 min-w-48 sm:min-w-64 max-w-[calc(100vw-2rem)]"
                        >
                          <div class="flex items-start gap-3">
                            <div class="shrink-0 mt-0.5">
                              <UIcon
                                name="i-lucide-alert-circle"
                                class="text-warning w-5 h-5"
                              />
                            </div>
                            <div>
                              <h3 class="font-semibold text-sm mb-1">
                                Archive Task
                              </h3>
                              <p class="text-sm text-muted">
                                Are you sure you want to archive this task? This
                                action cannot be undone.
                              </p>
                            </div>
                          </div>
                          <div class="flex items-center justify-end gap-2">
                            <UButton
                              color="neutral"
                              variant="ghost"
                              size="sm"
                              :disabled="archivingTaskId === task.id"
                              @click.stop="archivePopoverOpen[task.id] = false"
                            >
                              Cancel
                            </UButton>
                            <UButton
                              color="error"
                              variant="solid"
                              size="sm"
                              :loading="archivingTaskId === task.id"
                              :disabled="archivingTaskId === task.id"
                              @click.stop="handleArchiveTask(task.id)"
                            >
                              Archive
                            </UButton>
                          </div>
                        </div>
                      </template>
                    </UPopover>
                  </div>
                </div>
              </template>

              <Transition name="task-collapse">
                <div
                  v-show="!isTaskCollapsed(task.id)"
                  class="flex relative overflow-hidden w-full"
                  :class="imgRatio"
                >
                  <!-- 请求中的加载动画 -->
                  <div
                    v-if="
                      isWorking &&
                      task.id === activeTaskId &&
                      !isGeneratingUniqueId
                    "
                    class="w-full h-full flex flex-col items-center justify-center"
                  >
                    <div class="flex justify-center items-center py-2">
                      <div
                        class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"
                      ></div>
                    </div>
                    <p class="mt-3 text-center text-sm text-muted">
                      Creating task…
                    </p>
                  </div>

                  <!-- 预测中状态的加载动画 -->
                  <div
                    v-else-if="isTaskPending(task)"
                    class="w-full h-full flex flex-col items-center justify-center"
                  >
                    <div class="flex justify-center items-center py-2">
                      <div
                        class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"
                      ></div>
                    </div>
                    <p class="mt-3 text-center text-sm text-muted">
                      {{
                        isTaskPending(task)
                          ? "Processing…"
                          : "No image available"
                      }}
                    </p>
                  </div>

                  <!-- 图片显示 -->
                  <TaskOutputImages
                    v-else-if="isShowImage && getTaskOutputUrls(task).length"
                    :urls="getTaskOutputUrls(task)"
                    :alt="task.prompt || 'Generated image'"
                    v-model:active-index="taskOutputActiveIndex[task.id]"
                    class="w-full h-full"
                    :id="`task-${task.id}-image`"
                  />

                  <!-- 视频显示 -->
                  <video
                    class="object-contain w-full h-full"
                    v-else-if="task.outPutImageUrl && isShowVideo"
                    autoplay
                    muted
                    playsinline
                    controls
                    @loadstart="isVideoLoading = true"
                    @canplay="isVideoLoading = false"
                    @error="isVideoLoading = false"
                  >
                    <source :src="task.outPutImageUrl" type="video/mp4" />
                  </video>

                  <!-- 音频显示 -->
                  <VinylRecordPlayer
                    v-else-if="isShowRecord"
                    class="w-full h-full"
                    :audio-src="task.outPutImageUrl"
                    :player-size="0"
                  />

                  <!-- 图片对比显示 -->
                  <ImageComparator
                    v-else-if="isShowImageComparator"
                    :left-image="task.inPutImageUrls[0]"
                    :right-image="task.outPutImageUrl"
                  />

                  <!-- 无图片 -->
                  <div
                    v-else
                    class="w-full h-full flex items-center justify-center"
                  >
                    <p class="text-gray-500">No image available</p>
                  </div>
                </div>
              </Transition>

              <template #footer>
                <Transition name="task-collapse">
                  <div
                    v-show="!isTaskCollapsed(task.id)"
                    class="flex flex-wrap items-center gap-2 h-auto py-2 xl:py-0 shrink-0"
                  >
                    <!-- 移动端：使用下拉菜单 -->
                    <div class="flex xl:hidden items-center gap-2 w-full">
                      <UDropdownMenu :items="getTaskDropdownItems(task)">
                        <UButton
                          class="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
                          icon="nrk:more"
                          color="neutral"
                          variant="ghost"
                          title="More"
                          :disabled="isWorking || isLoadingInputImages"
                          >More
                        </UButton>
                      </UDropdownMenu>
                      <div class="ml-auto flex items-center gap-2 w-auto">
                        <!-- 左侧：下载按钮 -->
                        <UButton
                          color="neutral"
                          variant="subtle"
                          icon="material-symbols-light:download-rounded"
                          :disabled="
                            !getTaskOutputUrls(task).length ||
                            isWorking ||
                            isTaskPending(task)
                          "
                          @click.stop="onDownloadTask(task)"
                        >
                        </UButton>
                        <!-- 右侧：生成视频按钮 -->
                        <UButton
                          class="text-white flex items-center"
                          icon="material-symbols-light:star-shine-outline-rounded"
                          variant="solid"
                          color="primary"
                          title="Generate Video"
                          v-if="isShowGenerateVideo"
                          :disabled="
                            isWorking ||
                            isLoadingInputImages ||
                            isTaskPending(task)
                          "
                          @click.stop="onGenerateVideo(task)"
                        >
                        </UButton>
                      </div>
                    </div>

                    <!-- 桌面端：原有按钮 -->
                    <div class="hidden xl:flex items-center gap-2">
                      <!-- 重新生成按钮 -->
                      <UButton
                        v-if="canOperateTask(task)"
                        color="primary"
                        variant="outline"
                        icon="material-symbols-light:star-shine-outline-rounded"
                        :loading="isLoadingInputImages"
                        :disabled="
                          isWorking ||
                          isLoadingInputImages ||
                          isTaskPending(task)
                        "
                        @click.stop="onRegenerateTask(task)"
                      >
                        {{
                          isLoadingInputImages
                            ? "Loading..."
                            : sampleConfig?.regenerateBtnLabel || "Regenerate"
                        }}
                      </UButton>
                      <!-- 重新编辑按钮 -->
                      <UButton
                        v-if="isShowReEditImage && canOperateTask(task)"
                        color="neutral"
                        variant="outline"
                        icon="material-symbols-light:edit-road-rounded"
                        :loading="isLoadingInputImages"
                        :disabled="
                          isWorking ||
                          isLoadingInputImages ||
                          isTaskPending(task)
                        "
                        @click.stop="onReeditTask(task)"
                      >
                        {{ isLoadingInputImages ? "Loading..." : "Re-edit" }}
                      </UButton>
                      <!-- 文生图-编辑图片按钮 -->
                      <!-- <UButton
                      color="neutral"
                      variant="outline"
                      icon="material-symbols-light:photo-library-rounded"
                      :loading="isLoadingInputImages"
                      :disabled="
                        isWorking || isLoadingInputImages || isTaskPending(task)
                      "
                      @click.stop="onEditInImg2Img(task)"
                      v-if="isShowRefineImage"
                    >
                      {{ isLoadingInputImages ? "Loading..." : "Edit Image" }}
                    </UButton> -->
                      <!-- 图生图-继续修改图片按钮 -->
                      <UButton
                        color="neutral"
                        variant="outline"
                        icon="material-symbols-light:photo-library-rounded"
                        :loading="isLoadingInputImages"
                        :disabled="
                          isWorking ||
                          isLoadingInputImages ||
                          isTaskPending(task)
                        "
                        @click.stop="onRefineInImg2Img(task)"
                        v-if="isShowRefineImage"
                      >
                        {{ isLoadingInputImages ? "Loading..." : "Refine" }}
                      </UButton>
                    </div>
                    <div class="hidden xl:flex ml-auto items-center space-x-3">
                      <!-- 下载按钮 -->
                      <UButton
                        color="neutral"
                        variant="subtle"
                        icon="material-symbols-light:download-rounded"
                        :disabled="
                          !getTaskOutputUrls(task).length ||
                          isWorking ||
                          isTaskPending(task)
                        "
                        @click.stop="onDownloadTask(task)"
                      >
                        Download
                      </UButton>

                      <!-- Generate Video Button -->
                      <UButton
                        class="text-white flex items-center gap-2"
                        icon="material-symbols-light:star-shine-outline-rounded"
                        variant="solid"
                        color="primary"
                        title="Generate Video"
                        v-if="isShowGenerateVideo"
                        :disabled="
                          isWorking ||
                          isLoadingInputImages ||
                          isTaskPending(task)
                        "
                        @click.stop="onGenerateVideo(task)"
                      >
                        Generate Video
                      </UButton>
                    </div>
                  </div>
                </Transition>
              </template>
            </UCard>
          </TransitionGroup>
        </template>
      </div>
    </UCard>
    <!-- #endregion -->
  </div>
  <audio
    ref="mergeBgmAudioRef"
    class="hidden"
    preload="none"
    :src="mergeBgmAudioSrc || undefined"
    @play="onMergeBgmPlay"
    @playing="onMergeBgmPlaying"
    @pause="onMergeBgmPause"
    @ended="onMergeBgmPause"
    @waiting="onMergeBgmWaiting"
    @error="onMergeBgmPause"
  />
</template>

<script setup>
import VinylRecordPlayer from "~/components/ai-tools/VinylRecordPlayer.vue";
import { formatJSONWithLinks } from "@/common/utils/dev";
import CreditCalculator from "@/common/utils/credit-calculator";
import { getFirstFileMediaDuration } from "@/common/utils/media-duration";
import ImageComparator from "@/components/ai-tools/ImageComparator.vue";
import {
  reactive,
  computed,
  ref,
  onMounted,
  watch,
  onUnmounted,
  onDeactivated,
  onActivated,
} from "vue";
import { useState, useToast, navigateTo, nextTick } from "#imports";
import { useStore } from "vuex";

import TaskOutputImages from "@/components/ai-tools/TaskOutputImages.vue";
import MediaUploader from "@/components/ai-tools/MediaUploader.vue";
import { usePanelGridAutoHeight } from "@/composables/usePanelGridAutoHeight";
import TipLabel from "@/components/ai-tools/TipLabel.vue";
import EffectRecommendationsPanel from "@/components/ai-effects/EffectRecommendationsPanel.vue";
import { normalizeModelsByTool } from "@/composables/useAiR2ModelsAndEffects";
import {
  resolveFetchAssetUrl,
  resolveUserR2Url,
  stripKnownR2Domain,
} from "@/utils/r2Url";
const { openImagePreview } = useImagePreview();
const { panelGridRef, panelCardStyle } = usePanelGridAutoHeight();

// 定义 props 来接收 tool 参数
const props = defineProps({
  toolName: {
    type: String,
    required: true,
    default: "text-to-image",
  },
  toolTitle: {
    type: String,
    required: true,
    default: "Text to Image",
  },
  sampleConfig: {
    type: Object,
  },
  toolPageConfig: {
    type: Object,
    required: true,
  },
});

// 当前工具名称
const toolName = computed(() => props.toolName);
const toolTitle = computed(() => props.toolTitle);
const cdnUrl = useRuntimeConfig().public.cdnUrl;

// 工具 配置

// #region 右侧task卡片展示
const {
  // 右侧task卡片展示
  isShowImage = false, // 是否显示图片
  isShowVideo = false, // 是否显示视频
  isShowRecord = false, // 是否显示音频
  isShowImageComparator = false, // 是否显示图片对比
  isInputImage = false, // 是否输入图片

  // 右侧task卡片底部 按钮
  isShowReEditImage = false, // 是否显示编辑图片 重新填充提示词等到左侧
  isShowRefineImage = false, // 是否显示细化图片 将输出的图片重新调用ai生成
  isShowGenerateVideo = false, // 是否显示生成视频 将输出的图片生成视频
} = props.toolPageConfig || {};

// #endregion

// 用户信息
const store = useStore();
const token = computed(() => store.state.user.token);
const userInfo = computed(() => store.state.user.userInfo);
const userId = computed(() => userInfo.value.user_info_id);

const { $bus, $S3, $axios } = useNuxtApp();
const toast = useToast();
const imgRatio = ref("aspect-[16/9]");

/**绑定粘性观察器 */
let stickyResizeObserver = null;
let stickyObserverRetryTimer = null;

/**解除粘性观察器 */
const unbindStickyObserver = () => {
  if (stickyObserverRetryTimer) {
    clearTimeout(stickyObserverRetryTimer);
    stickyObserverRetryTimer = null;
  }
  if (stickyResizeObserver) {
    stickyResizeObserver.disconnect();
    stickyResizeObserver = null;
  }
};

// 使用全局认证模态框
const { openSignUpModal } = useAuthModals();

// 使用积分弹窗 composable
const { openNotEnoughCreditsModal, openBuyCreditsModal } = useCreditsModal();

// 全部model
const allModels = ref([]);

// 当前工具启用中的 model
const enabledModels = ref([]);

// 全部版本列表
const allModelVersions = ref([]);

// 当前工具启用中的版本列表
const enabledModelVersions = ref([]);

// Model 对象Title
const modelTitles = ref([]);

// 当前模型标题
const currentModelTitle = ref("");

/** Model 对象配置 */
const modelConfig = ref([]);

// 卡片标题
const cardTitleItems = computed(() => {
  if (!token.value) {
    return [
      {
        label: "Effects",
        value: "effects",
      },
    ];
  }

  return [
    {
      label: "Tasks",
      value: "tasks",
    },
    {
      label: "Effects",
      value: "effects",
    },
  ];
});

//
const cardTitleTab = ref(token.value ? "tasks" : "effects");

/** 当前表单 */
const form = reactive({});
const effectRecommendationKey = computed(
  () => `tool:${toolName.value || ""}:${form.model || ""}`
);

// 获取路由实例
const route = useRoute();

/** 是否正在生成 */
const isWorking = ref(false);

/** 是否正在生成唯一id */
const isGeneratingUniqueId = ref(false);

/** 是否已发送 API 请求 */
const isApiRequestSent = ref(false);

/** 是否正在取消 */
const isCancelling = ref(false);

/** 是否正在加载历史记录 */
const isLoadingHistory = ref(false);

/** 是否正在下载输入图片 */
const isLoadingInputImages = ref(false);

/** 是否是重新编辑任务 */
const isEditTask = ref(false);

/** 归档确认弹窗状态 */
const archivePopoverOpen = ref({});

/** 归档加载状态 */
const archivingTaskId = ref(null);

/** 刷新单个任务的加载状态 */
const refreshingTaskId = ref(null);

/** 刷新全部任务的加载状态 */
const isRefreshingAll = ref(false);

/** 视频加载状态 */
const isVideoLoading = ref(true);

/** 任务卡片折叠状态 */
const taskCollapsed = ref({});

/** 设置用户积分 */
const setUserCreditsSum = (credits) =>
  store.dispatch("user/setUserCreditsSum", credits);

/** 否是开发环境 */
const isDev = computed(() => store.state.user.is_dev);

/** 获取用户积分 */
const currentCredits = computed(() => userInfo.value?.credits_sum || 0);

/** 当前模型字段 */
const currentModelFields = computed(
  () => selectedModel(form.model)?.input_verify?.fields
);

/** 当前模型必填字段 */
const requiredMap = computed(
  () => selectedModel(form.model)?.input_verify?.required
);

/** 是否显示字段 */
const shouldShowField = (fieldDef, form) => {
  const s = fieldDef?.showIf;
  if (!s) return true;

  const v = form?.[s.field];

  if ("op" in s && "value" in s) {
    let conditionMet = false;
    switch (s.op) {
      case "eq":
        conditionMet = v === s.value;
        break;
      case "neq":
        conditionMet = v !== s.value;
        break;
      case "in":
        conditionMet = Array.isArray(s.value) && s.value.includes(v);
        break;
      default:
        conditionMet = false;
    }
    // show===false 表示条件成立时隐藏，否则条件成立时显示
    if ("show" in s && s.show === false) {
      return !conditionMet;
    }
    return conditionMet;
  }

  // 没有条件表达式，默认显示
  return true;
};

const shouldRenderField = (fieldDef, form) => {
  return fieldDef?.isVisible !== false && shouldShowField(fieldDef, form);
};

/** 应用选项覆盖 */
const applyOptionOverrides = (baseOptions, overrides, form) => {
  if (!Array.isArray(overrides) || overrides.length === 0) return baseOptions;
  let opts = baseOptions;
  for (const rule of overrides) {
    const when = rule?.when;
    if (!when) continue;
    const v = form?.[when.field];
    let hit = false;

    if ("op" in when && "value" in when) {
      switch (when.op) {
        case "eq":
          hit = v === when.value;
          break;
        case "neq":
          hit = v !== when.value;
          break;
        case "in":
          hit = Array.isArray(when.value) && when.value.includes(v);
          break;
        default:
          hit = false;
      }
    }

    if (hit) opts = rule.options;
  }
  return opts;
};

/** 切换任务卡片折叠状态（右侧只允许一个任务展开，展开第二个时自动折叠上一个） */
function toggleTaskCollapse(taskId) {
  const isCurrentlyCollapsed = isTaskCollapsed(taskId);
  setActive(taskId);
  if (isCurrentlyCollapsed) {
    // 展开当前：先把所有任务折叠，再展开当前
    const next = {};
    (displayedTasks.value || tasks.value).forEach((t) => {
      next[t.id] = t.id !== taskId;
    });
    taskCollapsed.value = next;
  } else {
    // 折叠当前
    taskCollapsed.value[taskId] = true;
  }
}

/** 获取任务卡片是否折叠 */
function isTaskCollapsed(taskId) {
  if (Object.prototype.hasOwnProperty.call(taskCollapsed.value, taskId)) {
    return taskCollapsed.value[taskId];
  }
  return activeTaskId.value === taskId ? false : true;
}

/** 初始化表单 */
const initFormByFields = (fields) => {
  const f = {};
  const context = { ...form, tool_name: props.toolName };

  for (const [key, def] of Object.entries(fields || {})) {
    f[key] = getFieldDefaultValue(key, def, context);
  }
  // console.log(new Error().stack);
  return f;
};

/** 处理路由参数 */
const handleRouteParams = (currentRoute) => {
  // 从 currentRoute.query 中获取 model 参数
  const modelParams = currentRoute.query.model;
  if (enabledModelVersions.value?.length === 0) return;

  if (modelParams) {
    const model = enabledModelVersions.value.find(
      (m) =>
        m.title.toLowerCase().replace(/\s+/g, "-") ===
        modelParams.toLowerCase().replace(/\s+/g, "-")
    );
    if (model) {
      form.model = model.key;
      currentModelTitle.value = model.title;
    }
    form.isPublic = true;
  }

  if (enabledModelVersions.value.length > 0) {
    const currentModelIds = enabledModelVersions.value.map((item) => item.key);
    // 如果当前 form.model 不在新的模型列表中，或者 form.model 为空，则使用第一个模型
    if (!form.model || !currentModelIds.includes(form.model)) {
      form.model = enabledModelVersions.value[0].key;
      form.isPublic = true;
    }
  }
};

const normalizeRoutePrompt = (prompt) => {
  const value = Array.isArray(prompt) ? prompt[0] : prompt;
  return typeof value === "string" ? value.trim() : "";
};

const getRoutePromptTargetField = (fields, context) => {
  const entries = Object.entries(fields).filter(([, def]) =>
    shouldRenderField(def, context)
  );
  const toTarget = ([fieldKey, def]) => ({
    fieldKey,
    paramsKey: def?.params_key || "",
  });

  const exactPromptEntry = entries.find(([fieldKey]) => fieldKey === "prompt");
  if (exactPromptEntry) return toTarget(exactPromptEntry);

  const paramsPromptEntry = entries.find(([, def]) =>
    String(def?.params_key || "")
      .toLowerCase()
      .includes("prompt")
  );
  if (paramsPromptEntry) return toTarget(paramsPromptEntry);

  const fieldPromptEntry = entries.find(([fieldKey]) =>
    fieldKey.toLowerCase().includes("prompt")
  );
  if (fieldPromptEntry) return toTarget(fieldPromptEntry);

  const textareaEntry = entries.find(
    ([, def]) =>
      def?.ui?.component === "textarea" && def?.params_type === "string"
  );
  return textareaEntry ? toTarget(textareaEntry) : null;
};

const appliedRoutePrompt = ref("");

watch(
  [() => route.query.prompt, currentModelFields],
  ([routePrompt]) => {
    const prompt = normalizeRoutePrompt(routePrompt);
    const fields = currentModelFields.value || {};
    const context = { ...form, tool_name: props.toolName };
    const target = getRoutePromptTargetField(fields, context);
    const targetField = target?.fieldKey || "";
    const targetParamsKey = target?.paramsKey || "";

    if (!prompt) {
      appliedRoutePrompt.value = "";
      return;
    }
    if (!targetField) return;
    if (appliedRoutePrompt.value === prompt) return;

    form[targetField] = prompt;
    if (targetParamsKey) form[targetParamsKey] = prompt;
    appliedRoutePrompt.value = prompt;
  },
  { immediate: true, flush: "post" }
);

/** 初始化模型配置 */
const initModelConfig = (rawData) => {
  if (!rawData) return;

  allModels.value = rawData?.[toolName.value];
  // 如果是空就抛出404
  if (!allModels.value) {
    throw createError({
      statusCode: 404,
      statusMessage: "Page Not Found",
      fatal: true,
    });
  }

  enabledModels.value = Object.fromEntries(
    Object.entries(allModels.value).map(([title, models]) => [
      title,
      (Array.isArray(models) ? models : []).filter(
        (model) => model?.enabled === undefined || !!Number(model.enabled)
      ),
    ])
  );

  modelTitles.value = Object.entries(enabledModels.value)
    .filter(([, value]) => value.length > 0)
    .map(([key, value]) => {
      // 获取数组中的第一个元素
      const firstItem = value[0];

      return {
        key: key,
        label: key,
        avatar: firstItem ? firstItem.avatar : null, // 容错处理
      };
    });

  allModelVersions.value = Object.entries(allModels.value).flatMap(
    ([title, models]) => {
      return Array.isArray(models)
        ? models.map((model) => ({ ...model, title }))
        : [];
    }
  );

  enabledModelVersions.value = Object.entries(enabledModels.value).flatMap(
    ([title, models]) => {
      return Array.isArray(models)
        ? models.map((model) => ({ ...model, title }))
        : [];
    }
  );

  currentModelTitle.value = modelTitles.value?.[0]?.key || "";
  console.log("初始数据获取完成");
};

watch(
  () => route.query.model, // 仅监听 query.model 的变化
  (newModel, oldModel) => {
    // 只有当新旧值不同时才执行，但由于我们监听的是属性，它通常在变化时就会触发
    if (newModel !== oldModel) {
      console.log(`模型参数已从 ${oldModel} 切换到 ${newModel}`);
      // 重新执行处理逻辑，传入当前的 route 对象
      handleRouteParams(route);
    }
  },
  { immediate: true } // 可选：设置 immediate: true 可以让 watch 在组件初始化时也执行一次，
);

const r2CachedFetch = useR2CachedFetch();

const { data: fetchModels, error: fetchModelsError } = await useAsyncData(
  "fetchModels",
  async () => {
    const data = await r2CachedFetch("/api/r2/ai/models.json");
    return data;
  },
  {
    transform: normalizeModelsByTool,
  }
);

/** 当前模型 */
function selectedModel(model) {
  const result = allModelVersions.value.find((m) => m.key === model);
  return result;
}

const isMergeBgmField = (fieldKey) => fieldKey === "merge_bgm";

const pickMergeBgmAudioSrc = (...sources) => {
  for (const source of sources) {
    const src = source?.merge_bgm_mp3 ?? source?.mergeBgmMp3;
    if (typeof src === "string" && src.trim()) return src.trim();
  }
  return "";
};

const mergeBgmAudioSrc = computed(() =>
  pickMergeBgmAudioSrc(
    selectedModel(form.model),
    props.sampleConfig,
    props.toolPageConfig
  )
);

const mergeBgmAudioRef = ref(null);
const mergeBgmIsPlaying = ref(false);
const mergeBgmIsLoading = ref(false);
const mergeBgmShowVisualizer = ref(false);

const stopMergeBgmPreview = () => {
  const audio = mergeBgmAudioRef.value;
  if (audio) {
    audio.pause();
    audio.currentTime = 0;
  }
  mergeBgmIsPlaying.value = false;
  mergeBgmIsLoading.value = false;
  mergeBgmShowVisualizer.value = false;
};

const onMergeBgmPlay = () => {
  mergeBgmIsPlaying.value = true;
};

const onMergeBgmPlaying = () => {
  mergeBgmIsLoading.value = false;
  mergeBgmShowVisualizer.value = true;
};

const onMergeBgmWaiting = () => {
  mergeBgmIsLoading.value = true;
  mergeBgmShowVisualizer.value = false;
};

const onMergeBgmPause = () => {
  mergeBgmIsPlaying.value = false;
  mergeBgmIsLoading.value = false;
  mergeBgmShowVisualizer.value = false;
};

const toggleMergeBgmPreview = async () => {
  if (!mergeBgmAudioSrc.value) return;
  const audio = mergeBgmAudioRef.value;
  if (!audio) return;

  if (mergeBgmIsPlaying.value || mergeBgmIsLoading.value) {
    stopMergeBgmPreview();
    return;
  }

  mergeBgmIsLoading.value = true;
  try {
    await audio.play();
  } catch {
    onMergeBgmPause();
  }
};

watch(mergeBgmAudioSrc, () => {
  stopMergeBgmPreview();
});

function isModelEnabled(modelKey) {
  const enabled = selectedModel(modelKey)?.enabled;
  if (enabled === undefined || enabled === null) return true;
  return !!Number(enabled);
}

function canOperateTask(task) {
  if (!task?.model) return true;
  return isModelEnabled(task.model);
}

function ensureTaskModelEnabled(task, actionLabel) {
  if (canOperateTask(task)) return true;
  toast.add({
    title: "Model unavailable",
    description: `The model used by this task has been disabled, so ${actionLabel} is not available.`,
    color: "warning",
    icon: "i-lucide-alert-triangle",
  });
  return false;
}

/** 积分配置 */
function selectedCredit() {
  if (!form.model) {
    return 0;
  }
  const model = selectedModel(form.model);
  const model_cost_config = model?.credit;
  if (!model || !model_cost_config) {
    return 0;
  }
  const cost = CreditCalculator.calculateCost(
    model_cost_config,
    buildCreditInputPreview(form, getModelFields(form.model))
  );
  const credits = CreditCalculator.toCredits(
    cost,
    model_cost_config.credit_ratio
  );
  return credits * (form?.image_output_num || 1);
}

/** 检测媒体类型 */
function detectMediaType(fieldDef) {
  const fmts = (fieldDef.validate?.formats || []).join(",").toLowerCase();
  if (fmts.includes("mp3") || fmts.includes("wav") || fmts.includes("m4a"))
    return "audio";
  if (
    fmts.includes("mp4") ||
    fmts.includes("mov") ||
    fmts.includes("webm") ||
    fmts.includes("mkv")
  )
    return "video";
  return "image";
}

/** 获取模型字段 */
const getModelFields = (modelKey) =>
  selectedModel(modelKey)?.input_verify?.fields || {};

/** 获取上传字段 */
const getUploaderFieldEntries = (fields) =>
  Object.entries(fields || {}).filter(
    ([, def]) => def?.ui?.component === "uploader"
  );

/** 规范化上传值 */
const normalizeUploadValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter((v) => v !== null && v !== undefined);
  }
  if (typeof value === "string") return value ? [value] : [];
  return [];
};

const parseMaybeJson = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  if (typeof value === "object") return value;
  return fallback;
};

const resolveUserUploadValue = (value) =>
  normalizeUploadValue(value).map(resolveUserR2Url).filter(Boolean);

const stripR2UploadValue = (value) =>
  normalizeUploadValue(value).map(stripKnownR2Domain).filter(Boolean);

/**
 * 根据paramsKey构建嵌套结构的函数
 * @param {Object} obj - 目标对象
 * @param {string} path - 要设置的路径，例如 "field.subfield"
 * @param {any} value - 要设置的值
 */
const setNestedValue = (obj, path, value) => {
  const keys = path.split(".");
  let current = obj;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
    } else {
      if (!current[key]) current[key] = {}; // 创建嵌套对象
      current = current[key];
    }
  });
};

/** 构建上传字段映射 */
const buildUploadFieldMapFromInput = (fields, inputJson = {}) => {
  const map = {};
  for (const [fieldKey, def] of getUploaderFieldEntries(fields)) {
    const paramsKey = def.params_key;
    if (!paramsKey) continue;
    map[fieldKey] = resolveUserUploadValue(inputJson?.[paramsKey]);
  }

  const legacyImages = inputJson?.input_images || {};
  for (const [fieldKey, legacyValue] of Object.entries(legacyImages)) {
    if (!map[fieldKey]) {
      map[fieldKey] = resolveUserUploadValue(legacyValue);
    }
  }

  const legacyVideos = resolveUserUploadValue(inputJson?.video_files);
  if (legacyVideos.length > 0) {
    const videoField = getUploaderFieldEntries(fields).find(([, def]) => {
      return detectMediaType(def) === "video";
    });
    if (videoField) {
      const [fieldKey] = videoField;
      if (!map[fieldKey] || map[fieldKey].length === 0) {
        map[fieldKey] = legacyVideos;
      }
    }
  }

  const legacyAudios = resolveUserUploadValue(inputJson?.audio_files);
  if (legacyAudios.length > 0) {
    const audioField = getUploaderFieldEntries(fields).find(([, def]) => {
      return detectMediaType(def) === "audio";
    });
    if (audioField) {
      const [fieldKey] = audioField;
      if (!map[fieldKey] || map[fieldKey].length === 0) {
        map[fieldKey] = legacyAudios;
      }
    }
  }

  return map;
};

/** 构建表单值 */
const buildFormValuesFromInput = (fields, inputJson = {}) => {
  const values = {};
  for (const [fieldKey, def] of Object.entries(fields || {})) {
    const paramsKey = def.params_key;
    if (!paramsKey) continue;
    if (def.ui.component === "uploader") {
      values[fieldKey] = resolveUserUploadValue(inputJson?.[paramsKey]);
      continue;
    }
    const raw = inputJson?.[paramsKey];
    if (raw !== undefined) values[fieldKey] = raw;
  }
  return values;
};

/** 总结上传 */
const summarizeUploads = (fields, uploadFieldMap) => {
  const inPutImageUrls = [];
  const videoFiles = [];
  const audioFiles = [];
  for (const [fieldKey, def] of getUploaderFieldEntries(fields)) {
    const urls = uploadFieldMap?.[fieldKey] || [];
    const mediaType = detectMediaType(def);
    if (mediaType === "video") {
      videoFiles.push(...urls);
    } else if (mediaType === "audio") {
      audioFiles.push(...urls);
    } else {
      inPutImageUrls.push(...urls);
    }
  }
  return { inPutImageUrls, videoFiles, audioFiles };
};

const getFieldDefaultValue = (fieldKey, fieldDef, form) => {
  // 1. select / ratio：必须在当前 options 内
  // debugger;
  if (
    fieldDef.ui.component === "select" ||
    fieldDef.ui.component === "ratioSelect"
  ) {
    const base = fieldDef.options || [];
    const opts = applyOptionOverrides(base, fieldDef.optionOverrides, {
      ...form,
      tool_name: props.toolName,
    });

    if (!opts || opts.length === 0) return null;

    // 若 default 在 options 中 → 用 default
    if (fieldDef.default !== undefined) {
      const ok = opts.some((o) => (o.key ?? o) === fieldDef.default);
      if (ok) return fieldDef.default;
    }

    // 否则用第一个 option
    const first = opts[0];
    return first.key ?? first;
  }

  // 2. uploader：默认空数组
  if (fieldDef.ui.component === "uploader") {
    return [];
  }

  // 3. switch
  if (fieldDef.ui.component === "switch") {
    return JSON.parse(fieldDef.default) ?? false;
  }

  // 4. input-scrollbar / number
  if (fieldDef.params_type === "number") return JSON.parse(fieldDef.default);

  // 5. textarea / string
  if (fieldDef.params_type === "string") return fieldDef.default;

  return null;
};

const visibleEntries = computed(() => {
  return Object.entries(currentModelFields.value || {})
    .filter(([, def]) =>
      shouldRenderField(def, { ...form, tool_name: props.toolName })
    )
    .sort(([, a], [, b]) => {
      const pa = a?.priority ?? 9999;
      const pb = b?.priority ?? 9999;
      // priority 越小越靠前；同 priority 保持稳定（按 key 排）
      if (pa !== pb) return pa - pb;
      return String(a?.ui?.label ?? "").localeCompare(
        String(b?.ui?.label ?? "")
      );
    });
});

const isRequired = (fieldKey) => {
  const req = requiredMap.value?.[props.toolName] || [];
  return req.includes(fieldKey);
};

const selectItems = (fieldKey, fieldDef) => {
  const base = fieldDef.options || [];
  const opts = applyOptionOverrides(base, fieldDef.optionOverrides, {
    ...form,
    tool_name: props.toolName,
  });
  const unit = fieldDef.ui.unit;
  // 支持两种：["mp4"] / [4,6,8] / [{key,label}]
  return opts.map((o) => {
    if (typeof o === "object") return { label: o.label ?? o.key, value: o.key };
    if (unit) {
      return { label: `${String(o)} ${unit}`, value: o };
    }
    return { label: String(o), value: o };
  });
};

const singleOption = (fieldKey, fieldDef) => {
  const items = selectItems(fieldKey, fieldDef);
  return items.length <= 1;
};

const ratioItems = (fieldKey, fieldDef) => {
  const base = fieldDef.options || [];
  const opts = applyOptionOverrides(base, fieldDef.optionOverrides, {
    ...form,
    tool_name: props.toolName,
  });
  return opts.map((o) => ({
    label: o.label,
    value: o.key,
    icon: o.icon,
  }));
};

const ratioIcon = (fieldKey, fieldDef) => {
  const items = ratioItems(fieldKey, fieldDef);
  return items.find((x) => x.value === form[fieldKey])?.icon;
};

const hasVisibleImageUploader = computed(() => {
  return Object.values(currentModelFields.value || {}).some((def) => {
    if (def?.ui?.component !== "uploader") return false;
    if (!shouldRenderField(def, { ...form, tool_name: props.toolName }))
      return false;
    return detectMediaType(def) === "image";
  });
});

const enableUploaderCropper = computed(() => {
  return hasVisibleImageUploader.value;
});

const RESERVED_KEYS = new Set(["model", "isPublic"]); // 永远保留

watchEffect(() => {
  const fields = currentModelFields.value || {};

  // 1) 确保固定字段存在（避免某些时机被误删/被覆盖成 undefined）
  if (form.isPublic === undefined) form.isPublic = true;
  // model 如果此时还没被 handleRouteParams 写入，先不强行设默认，交给你的 handleRouteParams
  // 但至少别让它变成空字符串以外的奇怪值
  if (form.model === undefined) form.model = ""; // 或者不写这一行也行

  const context = {
    ...form,
    tool_name: props.toolName,
    isPublic: form.isPublic,
  };

  // 2) 清理“多余键”：form 里存在，但 fields 里没有的字段键
  const allowedKeys = new Set([...Object.keys(fields), ...RESERVED_KEYS]);
  for (const k of Object.keys(form)) {
    if (!allowedKeys.has(k)) {
      delete form[k];
    }
  }

  // 3) 逐字段兜底修正（你原来的逻辑）
  for (const [fieldKey, fieldDef] of Object.entries(fields)) {
    const isVisible = shouldShowField(fieldDef, context);

    // 3.1 不可见 → 重置为默认值（防止隐藏字段污染提交）
    if (!isVisible) {
      const defaultValue = getFieldDefaultValue(fieldKey, fieldDef, context);
      if (form[fieldKey] !== defaultValue) {
        form[fieldKey] = defaultValue;
      }
      continue;
    } else {
      if (
        form[fieldKey] === null ||
        form[fieldKey] === undefined ||
        form[fieldKey] === ""
      ) {
        form[fieldKey] = getFieldDefaultValue(fieldKey, fieldDef, context);
      }
    }

    // 3.2 select / ratioSelect：值必须在 options 内
    if (
      fieldDef.ui.component === "select" ||
      fieldDef.ui.component === "ratioSelect"
    ) {
      const options = applyOptionOverrides(
        fieldDef.options || [],
        fieldDef.optionOverrides,
        context
      );

      if (options.length > 0) {
        const validValues = options.map((o) => o.key ?? o);

        if (!validValues.includes(form[fieldKey])) {
          form[fieldKey] = getFieldDefaultValue(fieldKey, fieldDef, context);
        }
      }
    }

    // 3.3 uploader：截断到 max
    if (fieldDef.ui.component === "uploader") {
      const max = fieldDef.validate?.max;
      const files = Array.isArray(form[fieldKey]) ? form[fieldKey] : [];
      if (max && files.length > max) {
        toast.add({
          title: `Maximum of ${max} files to upload`,
          color: "error",
          duration: Infinity, // 设置为无限大
        });
        form[fieldKey] = files.slice(0, max);
      }
    }
  }
});

/**模拟步进器 */
const handleLimit = (min, max, value) => {
  // 转换为数字
  let numericValue = parseFloat(value);

  // 如果值小于最小值，修正为最小值
  if (min !== undefined && numericValue < min) {
    return min;
  }

  // 如果值大于最大值，修正为最大值
  if (max !== undefined && numericValue > max) {
    return max;
  }

  // 返回原始值（如果在 min 和 max 范围内）
  return numericValue;
};

const getUploaderFieldsForForm = (modelKey) => {
  const fields = getModelFields(modelKey);
  return getUploaderFieldEntries(fields);
};

/** 上传文件总大小（字节） */
const inputFileSize = computed(() => {
  let total = 0;
  const uploaderEntries = getUploaderFieldsForForm(form.model);
  for (const [fieldKey] of uploaderEntries) {
    const files = Array.isArray(form[fieldKey]) ? form[fieldKey] : [];
    for (const file of files) {
      if (file && typeof file.size === "number") {
        total += file.size;
      }
    }
  }
  return total;
});

/** 上传文件总大小（MB，保留2位小数） */
const inputFileSizeMB = computed(() => {
  return (inputFileSize.value / (1024 * 1024)).toFixed(2);
});

/** 任务列表 */
const tasks = useState("tti:tasks", () => []);
const taskListOwnerKey = useState("tti:tasksOwnerKey", () => null);
const currentTaskListOwnerKey = computed(
  () => `ai-tools:${toolName.value || ""}`
);

// 多输出图片：记录每个任务当前选中的输出序号
const taskOutputActiveIndex = reactive({});

// 跟踪图片加载状态，避免重复请求
const imageLoadedStates = reactive({});
// 缓存已加载图片的 blob URL，完全避免重复网络请求
const imageBlobCache = new Map();

const resetReactiveObject = (target) => {
  Object.keys(target).forEach((key) => {
    delete target[key];
  });
};

// 将图片 URL 转换为 blob URL 并缓存
const getCachedImageUrl = async (url) => {
  // 如果已经有缓存的 blob URL，直接返回
  if (imageBlobCache.has(url)) {
    imageLoadedStates[url] = true;
    return imageBlobCache.get(url);
  }

  try {
    // 获取图片并转换为 blob
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // 缓存 blob URL
    imageBlobCache.set(url, blobUrl);
    imageLoadedStates[url] = true;

    return blobUrl;
  } catch (error) {
    console.error("Failed to cache image:", error);
    // 如果失败，返回原始 URL
    imageLoadedStates[url] = true;
    return url;
  }
};

// 处理图片加载
const handleImageLoad = (url, event) => {
  imageLoadedStates[url] = true;
  // 如果还没有缓存，尝试缓存
  if (!imageBlobCache.has(url) && event.target?.src) {
    getCachedImageUrl(url).catch(() => {
      // 静默失败，使用原始 URL
    });
  }
};

// 检查图片是否已经在浏览器缓存中
const checkImageCache = (url, imgElement) => {
  if (imgElement && imgElement.complete && imgElement.naturalHeight !== 0) {
    imageLoadedStates[url] = true;
    // 尝试缓存图片
    getCachedImageUrl(url).catch(() => {
      // 静默失败
    });
    return true;
  }
  return false;
};

const getTaskOutputUrls = (task) => {
  const raw = task?.outputUrls ?? task?.outPutImageUrl;
  return resolveUserUploadValue(raw);
};

const getTaskSelectedOutputUrl = (task) => {
  const urls = getTaskOutputUrls(task);
  if (!urls.length) return null;
  const idx = taskOutputActiveIndex?.[task.id] ?? 0;
  return urls[idx] ?? urls[0];
};

/** 显示的任务列表（最多10个） */
const displayedTasks = computed(() => {
  if (taskListOwnerKey.value !== currentTaskListOwnerKey.value) {
    return [];
  }
  return tasks.value.slice(0, 10);
});
const hasDisplayableTasks = computed(() => displayedTasks.value.length > 0);
const showTaskEmptyState = computed(
  () =>
    token.value &&
    cardTitleTab.value === "tasks" &&
    !isLoadingHistory.value &&
    !hasDisplayableTasks.value
);
const showTaskActions = computed(
  () => token.value && cardTitleTab.value === "tasks"
);

/** 当前任务ID */
const activeTaskId = useState("tti:activeId", () => null);

/** 判断任务是否在预测中 */
const isTaskPending = (task) => {
  return (
    task.status === "starting" ||
    task.status === "processing" ||
    task.status === "error" ||
    task.status === "created"
  );
};

/** 判断是否有预测中的任务 */
const hasPendingTasks = computed(() => {
  return tasks.value.some((task) => isTaskPending(task));
});

/** 定时器引用 */
const refreshTimer = ref(null);

/** 清理定时器的通用函数 */
const clearRefreshTimer = () => {
  if (refreshTimer.value) {
    console.log("清理定时器");
    clearTimeout(refreshTimer.value);
    refreshTimer.value = null;
  }
};

watch(
  fetchModels,
  (newData) => {
    if (fetchModelsError.value) {
      console.error("加载模型配置出错:", fetchModelsError.value);
      return;
    }
    if (newData) {
      initModelConfig(newData);
    }
  },
  { immediate: true }
);

watch(toolName, () => {
  if (fetchModels.value) {
    initModelConfig(fetchModels.value);
  }
});

// 仅保留必须在浏览器执行的逻辑：用户私有数据、待处理任务等
onMounted(async () => {
  try {
    if (fetchModelsError.value) {
      throw fetchModelsError.value;
    }

    const loaders = [];
    if (token.value) {
      loaders.unshift(loadUserTasks());
    }
    await Promise.all(loaders);

    await handlePendingTask();

    if (token.value) {
      cardTitleTab.value = hasDisplayableTasks.value ? "tasks" : "effects";
    }
  } catch (error) {
    console.error("初始化页面数据出错:", error);
  }
});

/** 异步递归定时器：等待刷新完成后再等待2秒 */
const startAutoRefresh = async () => {
  // 检查是否还有预测中的任务
  if (!hasPendingTasks.value) {
    console.log("没有预测中的任务，停止自动刷新");
    refreshTimer.value = null;
    return;
  }

  console.log("自动刷新预测中的任务", new Date().toISOString());

  try {
    // 等待刷新完成
    await refreshAllPendingTasks();
  } catch (error) {
    console.error("自动刷新出错:", error);
  }

  // 刷新完成后，等待4秒再进行下一次刷新
  if (hasPendingTasks.value) {
    refreshTimer.value = setTimeout(() => {
      startAutoRefresh();
    }, 4000);
  } else {
    console.log("所有任务已完成，停止自动刷新");
    refreshTimer.value = null;
  }
};

/** 监听预测中的任务，自动刷新状态 */
watch(
  hasPendingTasks,
  (hasPending) => {
    if (hasPending) {
      // 如果有预测中的任务且定时器未启动，启动异步递归定时器
      if (!refreshTimer.value) {
        console.log("启动定时器：每4秒自动刷新预测中的任务");
        startAutoRefresh();
      }
    } else {
      // 如果没有预测中的任务，清除定时器
      clearRefreshTimer();
    }
  },
  { immediate: true }
);

/** 组件激活时检查是否需要启动定时器（从 keep-alive 缓存中恢复时） */
onActivated(() => {
  console.log("组件激活时检查定时器 (onActivated)");
  // 如果有预测中的任务且定时器未运行，重新启动定时器
  if (hasPendingTasks.value && !refreshTimer.value) {
    console.log("检测到预测中的任务，重新启动定时器");
    startAutoRefresh();
  }
});

/** 组件失活时清理定时器（keep-alive 缓存时） */
onDeactivated(() => {
  console.log("组件失活时清理定时器 (onDeactivated)");
  clearRefreshTimer();
});

/** 组件卸载时清理定时器 */
onUnmounted(() => {
  console.log("组件卸载时清理定时器 (onUnmounted)");
  clearRefreshTimer();
  stopMergeBgmPreview();
});

/** 是否禁用生成按钮 */
const isGenerateDisabled = computed(() => {
  if (isWorking.value) return true;

  const fields = currentModelFields.value || {};
  const tool = toolName.value;
  const requiredKeys = requiredMap.value?.[tool] || [];

  for (const [key, def] of Object.entries(fields)) {
    // 1. 不显示的字段不参与校验
    if (!shouldShowField(def, { ...form, tool_name: tool })) continue;

    const val = form[key];

    const isRequired = requiredKeys.includes(key);

    // ===== 必填校验 =====
    if (isRequired) {
      if (def.ui.component === "uploader") {
        // debugger;
        if (!val || (Array.isArray(val) && val.length === 0)) {
          // debugger;
          return true;
        }
      } else {
        // debugger;
        if (val === null || val === undefined || val === "") {
          // debugger;
          return true;
        }
      }
    }

    // ===== validate 校验 =====
    if (def.validate) {
      // 字符串长度
      if (typeof val === "string" && val.length > 0) {
        if (def.validate.min && val.length < def.validate.min) return true;
        if (def.validate.max && val.length > def.validate.max) return true;
      }

      // 数字范围
      if (typeof val === "number") {
        if (def.validate.min !== undefined && val < def.validate.min)
          return true;
        if (def.validate.max !== undefined && val > def.validate.max)
          return true;
      }

      // 上传数量
      if (Array.isArray(val) && val.length > 0) {
        if (def.validate.max && val.length > def.validate.max) return true;
        if (def.validate.min && val.length < def.validate.min) return true;
      }
    }
  }

  return false;
});
/** 初始化任务折叠状态：第一个展开，其他折叠 */
function buildTaskCollapseState(taskList = [], expandedTaskId = null) {
  const next = {};
  taskList.forEach((task, index) => {
    const shouldExpand = expandedTaskId
      ? task.id === expandedTaskId
      : index === 0;
    next[task.id] = !shouldExpand;
  });
  return next;
}

function initTaskCollapseState(taskList = tasks.value, expandedTaskId = null) {
  taskCollapsed.value = buildTaskCollapseState(taskList, expandedTaskId);
}

function setTaskList(nextTasks, nextActiveTaskId = null) {
  const nextTaskList = Array.isArray(nextTasks) ? nextTasks : [];
  const hasPreferredActive = nextTaskList.some(
    (task) => task.id === nextActiveTaskId
  );
  const resolvedActiveTaskId = hasPreferredActive
    ? nextActiveTaskId
    : nextTaskList[0]?.id || null;
  taskCollapsed.value = buildTaskCollapseState(
    nextTaskList,
    resolvedActiveTaskId
  );
  activeTaskId.value = resolvedActiveTaskId;
  taskListOwnerKey.value = currentTaskListOwnerKey.value;
  tasks.value = nextTaskList;
}

/** 监听模型标题切换 */
let isFirstChange = true;
watch(
  () => currentModelTitle.value,
  (newModelTitle, oldModelTitle) => {
    if (newModelTitle !== oldModelTitle) {
      const result = (enabledModels.value?.[currentModelTitle.value] || []).map(
        (item) => ({
          ...item,
          value: item.key,
        })
      );
      form.model = result?.[0]?.key;
      modelConfig.value = result;

      const { model, ...restQuery } = route.query;
      const targetModel = currentModelTitle.value
        .toLowerCase()
        .replace(/\s+/g, "-");
      if (model !== targetModel && model && !isFirstChange) {
        const router = useRouter();
        router.replace({ query: restQuery });

        // 直接构造最终的状态传给处理函数，而不是从 route 对象中读取
        handleRouteParams({ ...route, query: restQuery });
      } else {
        handleRouteParams(route);
      }
      isFirstChange = false;
    }
  },
  { immediate: true }
);

/** 监听模型切换：更新当前模型标题 */
watch(
  () => form.model,
  (newModel) => {
    const model = selectedModel(newModel);
    if (model?.title) {
      currentModelTitle.value = model.title;
    }
  },
  { immediate: true }
);

/** 监听登录状态：登录成功后自动加载用户历史任务 */
watch(
  () => token.value,
  async (newToken, oldToken) => {
    // 当从未登录状态变为已登录状态时，加载用户历史任务
    if (newToken && !oldToken) {
      await loadUserTasks();
      cardTitleTab.value = hasDisplayableTasks.value ? "tasks" : "effects";
    } else if (!newToken && oldToken) {
      cardTitleTab.value = "effects";
    }
  }
);

// 获取用户套餐类型是否是Free
const isFree = computed(() => {
  return userInfo.value?.product_name === "Free";
});

/** 监听form.isPublic切换状态 */
watch(
  () => form.isPublic,
  (newIsPublic, oldIsPublic) => {
    if (!newIsPublic && !token.value) {
      form.isPublic = true;
      // 打开全局注册弹窗
      openSignUpModal();
    } else if (!newIsPublic && isFree.value) {
      form.isPublic = true;
      openBuyCreditsModal();
      toast.add({
        title: "Warning",
        color: "warning",
        icon: "i-lucide-alert-triangle",
        description:
          'Disabling the "Public" option is a premium feature. Upgrade your account to control your privacy settings.',
      });
    }
  }
);

// 创建预测任务
const buildInputJsonPreview = (payload, fields) => {
  const inputJson = {};
  const context = {
    ...form,
    tool_name: props.toolName,
    isPublic: form.isPublic,
  };
  for (const [fieldKey, def] of Object.entries(fields || {})) {
    const isVisible = shouldShowField(def, context);
    if (!isVisible) continue;
    const paramsKey = def.params_key;
    if (!paramsKey) continue;

    let value = payload[fieldKey];
    const paramsType = def.params_type; // 对value进行类型转换
    if (paramsType === "number") {
      value = Number(value);
    } else if (paramsType === "boolean") {
      value = Boolean(value);
    } else {
      value = value;
    }

    if (def.ui.component === "uploader") {
      const files = Array.isArray(payload[fieldKey]) ? payload[fieldKey] : [];
      const names = files
        .map((file) => file?.name)
        .filter((name) => Boolean(name));
      setNestedValue(
        inputJson,
        paramsKey,
        def.params_type === "list" ? names : names[0] || ""
      );
      continue;
    }

    setNestedValue(inputJson, paramsKey, value);
  }
  return inputJson;
};

const buildCreditInputPreview = (payload, fields) => {
  const inputJson = buildInputJsonPreview(payload, fields);
  const uploadDurationsByFieldKey = {};
  const uploadDurationsByParamsKey = {};

  for (const [fieldKey, def] of getUploaderFieldEntries(fields)) {
    const files = Array.isArray(payload[fieldKey]) ? payload[fieldKey] : [];
    const duration = getFirstFileMediaDuration(files);

    if (duration === null) continue;

    uploadDurationsByFieldKey[fieldKey] = duration;
    if (def?.params_key) {
      uploadDurationsByParamsKey[def.params_key] = duration;
    }
  }

  return {
    ...inputJson,
    __uploadDurationsByFieldKey: uploadDurationsByFieldKey,
    __uploadDurationsByParamsKey: uploadDurationsByParamsKey,
  };
};

const buildInputJsonWithUploads = async (payload, fields) => {
  const inputJson = {};
  const uploadFieldMap = {};
  for (const [fieldKey, def] of Object.entries(fields || {})) {
    checkCancelled();
    const paramsKey = def.params_key;
    if (!paramsKey) continue;

    let value = payload[fieldKey];
    const paramsType = def.params_type; // 对value进行类型转换
    if (paramsType === "number") {
      value = Number(value);
    } else if (paramsType === "boolean") {
      value = Boolean(value);
    }

    if (def.ui.component === "uploader") {
      checkCancelled();
      const files = Array.isArray(payload[fieldKey]) ? payload[fieldKey] : [];
      const urls =
        files.length > 0 ? await filesListToS3(payload.id, files) : [];
      checkCancelled();
      uploadFieldMap[fieldKey] = urls;
      setNestedValue(
        inputJson,
        paramsKey,
        def.params_type === "list" ? urls : urls[0] || ""
      );
      continue;
    }

    setNestedValue(inputJson, paramsKey, value);
  }
  return { inputJson, uploadFieldMap };
};

const buildTaskFromInputJson = (modelKey, inputJson, baseTask = {}) => {
  const fields = getModelFields(modelKey);
  const formValues = buildFormValuesFromInput(fields, inputJson);
  const uploadFieldMap = buildUploadFieldMapFromInput(fields, inputJson);
  const { inPutImageUrls, videoFiles, audioFiles } = summarizeUploads(
    fields,
    uploadFieldMap
  );

  const outputFormatFallback =
    inputJson?.output_format || inputJson?.output_image_format;
  if (outputFormatFallback && formValues.outputImageFormat === undefined) {
    formValues.outputImageFormat = outputFormatFallback;
  }

  return {
    ...baseTask,
    ...formValues,
    uploadFieldMap,
    inPutImageUrls,
    videoFiles,
    audioFiles,
  };
};

async function simulateGenerate(payload) {
  console.log(payload);
  try {
    checkCancelled();
    const fields = getModelFields(payload.model);
    checkCancelled();
    const { inputJson, uploadFieldMap } = await buildInputJsonWithUploads(
      payload,
      fields
    );
    checkCancelled();
    const { inPutImageUrls, videoFiles, audioFiles } = summarizeUploads(
      fields,
      uploadFieldMap
    );
    checkCancelled();
    console.log("payload", payload);
    const config = useRuntimeConfig();
    // 步骤三：创建预测任务，重试3次，只要res.status不是200都重试
    const fileCount = getUploaderFieldEntries(fields).reduce((acc, [key]) => {
      const files = Array.isArray(payload[key]) ? payload[key] : [];
      return acc + files.length;
    }, 0);
    console.log("文件总大小信息:", {
      inputFileSize: inputFileSize.value,
      inputFileSizeMB: inputFileSizeMB.value,
      fileCount,
    });

    checkCancelled();
    // 标记 API 请求即将发送，之后不能再取消
    isApiRequestSent.value = true;

    let res;
    let attempt = 0;
    const maxAttempts = 3;
    while (attempt < maxAttempts) {
      try {
        console.log("tmp inputJson", inputJson);
        res = await $axios({
          method: "post",
          url: "sharkfoto/ai_product",
          baseURL: config.public.apiBase,
          timeout: 2 * 60 * 1000, // 2分钟超时
          data: {
            id: payload.id,
            user_id: userId.value,
            inputFileSize: inputFileSize.value,
            fileCount: fileCount,
            toolName: toolName.value,
            model: payload.model,
            isPublic: payload.isPublic,
            inputJson: inputJson,
          },
        });
        if (res.status === 200) {
          break;
        } else {
          attempt++;
          if (attempt >= maxAttempts) {
            throw new Error(
              `Failed after ${maxAttempts} attempts, status: ${res.status}`
            );
          }
        }
      } catch (err) {
        attempt++;
        if (attempt >= maxAttempts) {
          throw err;
        }
      }
    }

    if (+res.data.code === 200) {
      const data = res.data.data;
      // 新的异步返回结构
      const task_id = data.task_id;
      const status = data.status;
      const input_json = data.input_json;
      const outputUrls = resolveUserUploadValue(data.outPutImageUrl);
      const outPutImageUrl = outputUrls[0] || null;
      const completed_at = data.completed_at;
      const model = data.model;
      const isPublic = data.isPublic;
      const credits_sum = data.credits_sum;
      await setUserCreditsSum(credits_sum);
      return buildTaskFromInputJson(model, input_json || inputJson, {
        id: task_id,
        model: model,
        status: status,
        isPublic: isPublic,
        outPutImageUrl: outPutImageUrl,
        outputUrls,
        createdAt: completed_at ? new Date(completed_at).getTime() : Date.now(),
        credits_sum: credits_sum,
        outputImageFormat: payload.outputImageFormat,
        inPutImageUrls,
        videoFiles,
        audioFiles,
      });
    } else {
      const error = res.data.error;

      if (error === "NotEnoughCredits") {
        // 自定义弹窗显示 积分不足
        const creditsNeeded = selectedCredit();
        openNotEnoughCreditsModal(creditsNeeded);
      }

      // 抛出错误
      throw new Error(error);
    }
  } catch (error) {
    // 如果是取消操作，直接抛出，不处理错误提示
    if (
      isCancelling.value ||
      error.message === "Generation cancelled by user"
    ) {
      throw error;
    }

    console.error("请求失败:", error);
    // 删除失败的任务，使用传入的 payload.id
    if (tasks.value.length > 0) {
      const failedTaskIndex = tasks.value.findIndex(
        (task) => task.id === payload.id
      );
      if (failedTaskIndex !== -1) {
        tasks.value.splice(failedTaskIndex, 1); // 删除失败的任务
      }
    }
    if (error.message !== "NotEnoughCredits") {
      // 向用户反馈请求失败
      toast.add({
        title: "Failed",
        color: "error",
        duration: Infinity, // 设置为无限大
        progress: false,
        orientation: "horizontal",
        icon: "i-lucide-alert-triangle",
        description: error.message,
        actions: [
          {
            icon: "i-lucide-copy",
            label: "Copy",
            color: "neutral",
            variant: "outline",
            onClick: (e) => {
              e?.stopPropagation();
              copyPrompt(error.message);
            },
          },
        ],
      });
      return;
    }
    throw error;
  }
}

// 步骤一: 生成唯一id
async function generateUniqueId(payload) {
  let attempts = 0;
  while (attempts < 3) {
    try {
      checkCancelled();
      isGeneratingUniqueId.value = true;
      checkCancelled();
      const fields = getModelFields(payload.model);
      const inputJsonPreview = buildInputJsonPreview(payload, fields);
      checkCancelled();
      const config = useRuntimeConfig();
      const res = await $axios({
        method: "get",
        url: "sharkfoto/ai_product",
        baseURL: config.public.apiBase,
        timeout: 2 * 60 * 1000, // 2分钟超时
        params: {
          user_id: userId.value,
          model: payload.model,
          isPublic: payload.isPublic,
          toolName: toolName.value,
          input_json: JSON.stringify(inputJsonPreview),
        },
      });
      checkCancelled();
      if (res.data.code === 200) {
        const uniqueId = res.data.data.task_id;
        isGeneratingUniqueId.value = false;
        return uniqueId;
      }
      // 其他异常情况也重试
      throw new Error(res.data?.error || "生成唯一id失败");
    } catch (error) {
      // 如果是取消操作，直接抛出
      if (
        isCancelling.value ||
        error.message === "Generation cancelled by user"
      ) {
        isGeneratingUniqueId.value = false;
        throw error;
      }
      attempts++;
      if (attempts >= 3) {
        isGeneratingUniqueId.value = false;
        // 弹窗，提示用户生成唯一ID失败
        toast.add({
          title: "Failed to generate task id",
          color: "error",
          duration: Infinity, // 设置为无限大
          icon: "i-lucide-alert-triangle",
          description:
            "Failed to generate a unique ID after 3 attempts. Please try again later.",
        });
        throw new Error("Failed to generate unique id after 3 attempts");
      }
      // 循环继续重试
      if (attempts < 3) {
        // 可根据需要延时重试，这里省略
        continue;
      }
    } finally {
      // 只有超过三次或者返回才设置为false
      if (attempts >= 3) {
        isGeneratingUniqueId.value = false;
      }
    }
  }
}

/** 取消生成 */
function onCancelGenerate() {
  isCancelling.value = true;
  // 清理状态会在 onGenerate 的 finally 中处理
}

/** 检查是否已取消 */
function checkCancelled() {
  if (isCancelling.value) {
    throw new Error("Generation cancelled by user");
  }
}

/** 生成 */
async function onGenerate(formData) {
  // return;
  // 判断是否登录
  if (!token.value) {
    // 打开全局注册弹窗
    openSignUpModal();
    return;
  }

  // 前端判断积分是否不足
  if (currentCredits.value < selectedCredit()) {
    openNotEnoughCreditsModal(selectedCredit());
    return;
  }

  // 重置状态
  isWorking.value = true;
  cardTitleTab.value = "tasks";
  isApiRequestSent.value = false;
  isCancelling.value = false;
  let pendingTaskId = null;

  try {
    checkCancelled();
    const id = await generateUniqueId(formData);
    pendingTaskId = id;
    checkCancelled();

    const now = Date.now();
    const pending = {
      ...formData,
      id: id,
      isArchived: false,
      createdAt: now,
      toolName: toolName.value,
    };
    setTaskList([pending, ...tasks.value], id);

    await nextTick();
    checkCancelled();

    // 平滑滚动到新卡片（临时ID）
    if (typeof document !== "undefined") {
      const container = document.getElementById("tasks-card");
      const el = container?.querySelector(`[data-task-id="${id}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }

    checkCancelled();
    const res = await simulateGenerate(pending);
    checkCancelled();

    console.log("生成响应:", res);
    const index = tasks.value.findIndex((t) => t.id === id);
    if (index !== -1) {
      tasks.value[index] = {
        ...tasks.value[index], // 保留原有属性
        ...res,
        isArchived: false,
      };

      activeTaskId.value = res.id; // 确保 activeTaskId 使用的是 res.id 而非临时id
      console.log("任务已创建，状态:", res.status, "tasks.value:", tasks.value);
      toast.add({
        title: "Task created",
        color: "success",
        icon: "i-lucide-check-circle",
      });

      // 更新 store 中的历史记录缓存（将新任务添加到缓存开头）
      const cachedHistory =
        store.getters["ai/getHistoryByTool"](toolName.value) || [];
      const updatedHistory = [
        tasks.value[index],
        ...cachedHistory.filter((t) => t.id !== res.id),
      ];
      store.commit("ai/SET_HISTORY_CACHE", {
        toolName: toolName.value,
        tasks: updatedHistory,
      });

      // 切换到tasks
      cardTitleTab.value = "tasks";

      // 强制 Vue 更新视图
      await nextTick();

      // 平滑滚动到新的正式ID
      if (typeof document !== "undefined") {
        const container2 = document.getElementById("tasks-card");
        const el2 = container2?.querySelector(`[data-task-id="${res.id}"]`);
        if (el2 && typeof el2.scrollIntoView === "function") {
          el2.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    }
  } catch (error) {
    // 如果是取消操作，清理 pending 任务
    if (
      isCancelling.value ||
      error.message === "Generation cancelled by user"
    ) {
      if (pendingTaskId) {
        const failedTaskIndex = tasks.value.findIndex(
          (task) => task.id === pendingTaskId
        );
        if (failedTaskIndex !== -1) {
          tasks.value.splice(failedTaskIndex, 1);
        }
      }
      toast.add({
        title: "Generation cancelled",
        color: "neutral",
        icon: "i-lucide-x-circle",
      });
    } else {
      // 其他错误继续抛出
      throw error;
    }
  } finally {
    isWorking.value = false;
    isApiRequestSent.value = false;
    isCancelling.value = false;
  }
}

/** 获取任务状态的通用函数 */
async function getTaskStatus(taskIds) {
  try {
    // debugger;
    const res = await store.dispatch("ai/getTaskStatus", {
      ids: taskIds.join(","),
      userId: userId.value,
    });
    if (res && res.data && res.status === 200) {
      const taskDatas = res.data.taskDatas;
      const credits_sum = res.data.credits_sum;
      await setUserCreditsSum(credits_sum);
      return taskDatas;
    }
  } catch (error) {
    console.error("获取任务状态失败:", error);
    throw new Error(error);
  }
  return null;
}

/** 刷新单个任务 */
async function refreshSingleTask(taskId) {
  refreshingTaskId.value = taskId;
  try {
    const task = await getTaskStatus([taskId]);
    // 更新任务状态
    await updateTaskStatus(task);
  } catch (error) {
    console.error("刷新任务失败:", error);
    toast.add({
      title: "Failed to refresh the task",
      color: "error",
      duration: Infinity, // 设置为无限大
      icon: "i-lucide-alert-triangle",
    });
  } finally {
    refreshingTaskId.value = null;
  }
}

/** 刷新所有预测中的任务 */
async function refreshAllPendingTasks() {
  const pendingTasks = tasks.value.filter((task) => isTaskPending(task));
  if (pendingTasks.length === 0) return;

  isRefreshingAll.value = true;
  try {
    const taskIds = pendingTasks.map((task) => task.id);
    const tasksList = await getTaskStatus(
      Array.isArray(taskIds) ? taskIds : [taskIds]
    );

    // 批量更新任务状态
    for (const task of tasksList) {
      await updateTaskStatus(task);
    }
  } catch (error) {
    console.error("刷新全部任务失败:", error);
    toast.add({
      title: "Failed to refresh all tasks",
      color: "error",
      duration: Infinity, // 设置为无限大
      icon: "i-lucide-alert-triangle",
    });
  } finally {
    isRefreshingAll.value = false;
  }
}

/** 更新任务状态的通用函数 */
async function updateTaskStatus(taskData) {
  const taskId = taskData.id;
  const status = taskData.status;
  const outputUrls = resolveUserUploadValue(taskData.output);
  const outPutImageUrl = outputUrls[0] || null;
  const completed_at = taskData.completed_at;
  const generateAudio = taskData.generateAudio;
  const creditCost = taskData.creditCost;

  // 如果任务失败，从列表中删除
  if (status === "failed" || status === "error") {
    setTaskList(
      tasks.value.filter((task) => task.id !== taskId),
      activeTaskId.value
    );
    const errorMessage = taskData.output;
    // 同时从缓存中删除
    const cachedHistory = store.getters["ai/getHistoryByTool"](toolName.value);
    if (cachedHistory && Array.isArray(cachedHistory)) {
      const updatedHistory = cachedHistory.filter((task) => task.id !== taskId);
      store.commit("ai/SET_HISTORY_CACHE", {
        toolName: toolName.value,
        tasks: updatedHistory,
      });
    }

    toast.add({
      title: "Failed",
      description: errorMessage,
      color: "error",
      duration: Infinity, // 设置为无限大
      icon: "i-lucide-x-circle",
      progress: false,
      orientation: "horizontal",
      actions: [
        {
          icon: "i-lucide-copy",
          label: "Copy",
          color: "neutral",
          variant: "outline",
          onClick: (e) => {
            e?.stopPropagation();
            copyPrompt(errorMessage);
          },
        },
      ],
    });
    return;
  }

  // 更新任务状态和数据
  const taskIndex = tasks.value.findIndex((task) => task.id === taskId);
  if (taskIndex !== -1) {
    const inputJson = taskData.input || {};
    const modelKey = taskData.model || tasks.value[taskIndex].model;
    tasks.value[taskIndex] = buildTaskFromInputJson(modelKey, inputJson, {
      ...tasks.value[taskIndex],
      status: status,
      outPutImageUrl: outPutImageUrl || tasks.value[taskIndex].outPutImageUrl,
      outputUrls: outputUrls.length
        ? outputUrls
        : tasks.value[taskIndex].outputUrls,
      createdAt: completed_at
        ? new Date(
            /\d{2}:\d{2}:\d{2}$/.test(completed_at)
              ? completed_at + "Z"
              : completed_at
          ).getTime()
        : tasks.value[taskIndex].createdAt,
      generateAudio: generateAudio,
      creditCost: creditCost,
    });

    // 如果任务成功完成，显示提示
    if (status === "succeeded") {
      toast.add({
        title: "Task completed",
        color: "success",
        icon: "i-lucide-check-circle",
      });
    }

    // 更新缓存
    const cachedHistory = store.getters["ai/getHistoryByTool"](toolName.value);
    if (cachedHistory && Array.isArray(cachedHistory)) {
      const updatedHistory = cachedHistory.map((task) => {
        if (task.id === taskId) {
          return {
            ...task,
            status: status,
            outPutImageUrl: outPutImageUrl || task.outPutImageUrl,
            outputUrls: outputUrls.length ? outputUrls : task.outputUrls,
            createdAt: new Date(
              /\d{2}:\d{2}:\d{2}$/.test(completed_at)
                ? completed_at + "Z"
                : completed_at
            ).getTime(),
          };
        }
        return task;
      });
      store.commit("ai/SET_HISTORY_CACHE", {
        toolName: toolName.value,
        tasks: updatedHistory,
      });
    }
  }
}

/** 处理归档任务（带加载状态和弹窗关闭） */
async function handleArchiveTask(id) {
  archivingTaskId.value = id;
  try {
    await onArchiveTask(id);
    // 归档成功后关闭弹窗
    archivePopoverOpen.value[id] = false;

    // 更新缓存中的任务状态，将其标记为已归档
    const cachedHistory = store.getters["ai/getHistoryByTool"](toolName.value);
    if (cachedHistory && Array.isArray(cachedHistory)) {
      const updatedHistory = cachedHistory.map((task) => {
        if (task.id === id) {
          return { ...task, isArchived: true };
        }
        return task;
      });
      store.commit("ai/SET_HISTORY_CACHE", {
        toolName: toolName.value,
        tasks: updatedHistory,
      });

      // 从更新后的缓存中重新获取最新的10个未归档任务
      const unarchivedTasks = updatedHistory.filter((task) => !task.isArchived);
      const latestTasks = unarchivedTasks.slice(0, 10);
      const nextActiveTaskId =
        activeTaskId.value === id ? null : activeTaskId.value;
      setTaskList(latestTasks, nextActiveTaskId);

      console.log(
        "归档后更新任务列表 - 缓存 (全部):",
        updatedHistory.length,
        "未归档:",
        unarchivedTasks.length,
        "显示:",
        latestTasks.length
      );
    }

    toast.add({
      title: "Task archived successfully",
      color: "success",
      icon: "i-lucide-check-circle",
    });
  } catch (error) {
    console.error("归档失败:", error);
    toast.add({
      title: "Failed to archive task",
      color: "error",
      duration: Infinity, // 设置为无限大
      icon: "i-lucide-alert-triangle",
    });
  } finally {
    archivingTaskId.value = null;
  }
}

/** 复制提示词 */
async function copyPrompt(text) {
  try {
    // 现代方法 是否归档
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof window !== "undefined" &&
      window.isSecureContext
    ) {
      await navigator.clipboard.writeText(text);
    } else if (typeof document !== "undefined") {
      // 传统方法
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    toast.add({
      title: "Copied",
      description: text,
      color: "neutral",
      icon: "i-lucide-copy",
      ui: {
        wrapper: "w-0 flex-1 flex flex-col max-h-48 overflow-y-auto",
      },
    });
  } catch (err) {
    console.error("Copy failed:", err);
    toast.add({
      title: "Copy failed",
      description: "Please copy the text manually",
      color: "error",
      duration: Infinity, // 设置为无限大
      icon: "i-lucide-alert-circle",
    });
  }
}

/**
 * 格式化时间，自动根据用户时区和本地习惯显示
 * 北京/中国等地区采用"年-月-日 时:分:秒"，欧美等地区采用"月/日/年 时:分:秒"
 */
// 传入时间戳并根据用户的地区自动格式化
function formatTime(timestamp) {
  // 获取用户的语言环境（默认使用浏览器语言）
  const userLocale =
    typeof navigator !== "undefined" ? navigator.language || "en-US" : "en-US";
  // 创建Date对象
  const date = new Date(timestamp);

  // 根据用户的语言环境和时区进行格式化
  const formatter = new Intl.DateTimeFormat(userLocale, {
    year: "numeric",
    month: "long", // 可以更改为'short'来显示简短月份名称
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // 格式化并返回
  return formatter.format(date);
}

/** 任务列表交互 */
function setActive(id) {
  activeTaskId.value = id;
}

/** 处理待处理的任务（从任务列表页面跳转过来）*/
async function handlePendingTask() {
  const pendingTask = store.state.ai.pendingTask;
  const pendingAction = store.state.ai.pendingAction;
  const isFirstModel = pendingTask?.isFirstModel || false;

  if (!pendingTask || !pendingAction) {
    return; // 没有待处理的任务
  }

  // 确保当前工具页面与任务的工具类型匹配
  if (pendingTask.toolName !== toolName.value) {
    console.warn("待处理任务的工具类型与当前页面不匹配");
    store.commit("ai/CLEAR_PENDING_TASK");
    return;
  }
  scrollToTop({ smooth: false });
  console.log("处理待处理的任务:", pendingTask, pendingAction);

  try {
    // 根据操作类型执行相应操作
    if (pendingAction === "regenerate") {
      await onRegenerateTask(pendingTask);
    } else if (pendingAction === "re-edit") {
      await onReeditTask(pendingTask, isFirstModel);
    }
  } finally {
    // 清除待处理的任务
    store.commit("ai/CLEAR_PENDING_TASK");
  }
}

/** 重新生成任务 */
const normalizeFormats = (formats = []) =>
  formats.map((fmt) => fmt.replace(/^\./, ""));

const getUploadFieldMapFromTask = (task, modelKey) => {
  if (task.uploadFieldMap) {
    const cloned = JSON.parse(JSON.stringify(task.uploadFieldMap));
    Object.keys(cloned).forEach((key) => {
      cloned[key] = stripR2UploadValue(cloned[key]);
    });
    return cloned;
  }

  const fields = getModelFields(modelKey);
  const map = {};

  if (task.inPutImages) {
    Object.entries(task.inPutImages).forEach(([key, value]) => {
      if (fields?.[key]?.ui?.component === "uploader") {
        map[key] = stripR2UploadValue(value?.urls || []);
      }
    });
  }

  if (Array.isArray(task.videoFiles) && task.videoFiles.length > 0) {
    const videoField = getUploaderFieldEntries(fields).find(([, def]) => {
      return detectMediaType(def) === "video";
    });
    if (videoField) {
      const [fieldKey] = videoField;
      map[fieldKey] = stripR2UploadValue(task.videoFiles);
    }
  }

  if (Array.isArray(task.audioFiles) && task.audioFiles.length > 0) {
    const audioField = getUploaderFieldEntries(fields).find(([, def]) => {
      return detectMediaType(def) === "audio";
    });
    if (audioField) {
      const [fieldKey] = audioField;
      map[fieldKey] = stripR2UploadValue(task.audioFiles);
    }
  }

  return map;
};

const buildFormDataFromTask = (task) => {
  const fields = getModelFields(task.model);
  const fresh = initFormByFields(fields);
  const formData = {
    ...JSON.parse(JSON.stringify(form)),
    ...fresh,
    model: task.model,
    isPublic:
      typeof task.isPublic !== "undefined"
        ? JSON.parse(JSON.stringify(task.isPublic))
        : form.isPublic,
  };

  for (const key of Object.keys(fields)) {
    if (task[key] !== undefined) {
      formData[key] = JSON.parse(JSON.stringify(task[key]));
    }
  }

  return { formData, fields };
};

const hydrateUploaderFields = async (formData, fields, uploadFieldMap) => {
  for (const [fieldKey, def] of getUploaderFieldEntries(fields)) {
    let urls = uploadFieldMap?.[fieldKey] || [];
    urls = urls.filter(
      (url) =>
        url !== null &&
        url !== undefined &&
        url !== "" &&
        JSON.stringify(url) !== "[]"
    );
    if (urls.length === 0) continue;
    const formats = normalizeFormats(def.validate?.formats || []);
    const files = await Promise.all(urls.map((url) => urlToFile(url, formats)));
    formData[fieldKey] = files;
  }
};

async function onRegenerateTask(task) {
  if (!ensureTaskModelEnabled(task, "regeneration")) return;
  const { formData, fields } = buildFormDataFromTask(task);
  const uploadFieldMap = getUploadFieldMapFromTask(task, task.model);

  try {
    isWorking.value = true;
    await hydrateUploaderFields(formData, fields, uploadFieldMap);
  } catch (error) {
    console.error("重新生成任务失败:", error);
    toast.add({
      title: "Failed",
      description: error.message,
      color: "error",
      duration: Infinity, // 设置为无限大
      icon: "i-lucide-alert-triangle",
      progress: false,
      orientation: "horizontal",
      actions: [
        {
          icon: "i-lucide-copy",
          label: "Copy",
          color: "neutral",
          variant: "outline",
          onClick: (e) => {
            e?.stopPropagation();
            copyPrompt(error.message);
          },
        },
      ],
    });
  } finally {
    isWorking.value = false;
  }

  await onGenerate(formData);
}

const URL_FILE_MIME_MAP = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  avif: "image/avif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
  wmv: "video/x-ms-wmv",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  ogg: "audio/ogg",
  flac: "audio/flac",
  aac: "audio/aac",
};

const MIME_EXTENSION_MAP = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/avif": "avif",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
  "video/x-msvideo": "avi",
  "video/x-matroska": "mkv",
  "video/x-ms-wmv": "wmv",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/mp4": "m4a",
  "audio/ogg": "ogg",
  "audio/flac": "flac",
  "audio/aac": "aac",
};

const OPAQUE_BINARY_EXTENSIONS = new Set(["octet-stream", "bin", "dat"]);

const isOpaqueBinaryMime = (mimeType = "") =>
  ["application/octet-stream", "binary/octet-stream"].includes(
    mimeType?.toLowerCase?.() || ""
  );

const getExtensionFromUrl = (url) => {
  try {
    const path = new URL(url).pathname;
    const fileName = path.split("/").pop() || "";
    const ext = fileName.includes(".") ? fileName.split(".").pop() : "";
    return ext?.toLowerCase() || "";
  } catch (error) {
    console.warn("URL path parsing failed");
    return "";
  }
};

const getFileNameFromUrl = (url, fallbackExt = "") => {
  let fileName = `file_${Date.now()}`;
  try {
    const decodedPath = decodeURIComponent(new URL(url).pathname);
    const extractedName = decodedPath.split("/").pop();
    if (extractedName && extractedName.includes(".")) {
      return extractedName;
    }
  } catch (error) {}

  if (fallbackExt) {
    return `${fileName}.${fallbackExt}`;
  }
  return fileName;
};

const replaceFileExtension = (fileName, nextExt) => {
  const safeName = fileName || `file_${Date.now()}`;
  const baseName = safeName.includes(".")
    ? safeName.replace(/\.[^.]+$/, "")
    : safeName;
  return `${baseName}.${nextExt}`;
};

const getPreferredImageOutputType = (formats = []) => {
  const supportedOutputTypes = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
  };

  for (const format of normalizeFormats(formats)) {
    if (supportedOutputTypes[format]) {
      return supportedOutputTypes[format];
    }
  }

  return "image/png";
};

const detectImageMimeFromBlob = async (blob) => {
  try {
    const buffer = await blob.slice(0, 32).arrayBuffer();
    const bytes = new Uint8Array(buffer);

    if (
      bytes.length >= 8 &&
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a
    ) {
      return "image/png";
    }

    if (
      bytes.length >= 3 &&
      bytes[0] === 0xff &&
      bytes[1] === 0xd8 &&
      bytes[2] === 0xff
    ) {
      return "image/jpeg";
    }

    if (
      bytes.length >= 6 &&
      bytes[0] === 0x47 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x38 &&
      (bytes[4] === 0x37 || bytes[4] === 0x39) &&
      bytes[5] === 0x61
    ) {
      return "image/gif";
    }

    if (
      bytes.length >= 12 &&
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      return "image/webp";
    }

    if (
      bytes.length >= 12 &&
      bytes[4] === 0x66 &&
      bytes[5] === 0x74 &&
      bytes[6] === 0x79 &&
      bytes[7] === 0x70 &&
      bytes[8] === 0x61 &&
      bytes[9] === 0x76 &&
      bytes[10] === 0x69 &&
      bytes[11] === 0x66
    ) {
      return "image/avif";
    }
  } catch (error) {
    console.warn("Image signature detection failed", error);
  }

  return "";
};

const loadImageFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = async () => {
      URL.revokeObjectURL(objectUrl);

      if (typeof createImageBitmap === "function") {
        try {
          const bitmap = await createImageBitmap(file);
          resolve(bitmap);
          return;
        } catch (bitmapError) {
          reject(
            new Error(
              `Image decode failed. createImageBitmap failed: ${
                bitmapError?.message || "Unknown error"
              }`
            )
          );
          return;
        }
      }

      reject(new Error("Image decode failed"));
    };
    img.src = objectUrl;
  });
};

const convertBlobToImageFile = async (
  blob,
  fileName,
  outputType = "image/png",
  sourceType = ""
) => {
  const sourceFile = new File([blob], fileName || `file_${Date.now()}`, {
    type: sourceType || blob.type || "application/octet-stream",
    lastModified: Date.now(),
  });
  const image = await loadImageFromFile(sourceFile);
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas not supported");
  }

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  const normalizedBlob = await new Promise((resolve, reject) => {
    canvas.toBlob((resultBlob) => {
      if (!resultBlob) {
        reject(new Error("Image conversion failed"));
        return;
      }
      resolve(resultBlob);
    }, outputType);
  });

  const finalType = normalizedBlob.type || outputType;
  const finalExt = MIME_EXTENSION_MAP[finalType] || "png";
  return new File(
    [normalizedBlob],
    replaceFileExtension(fileName || `file_${Date.now()}`, finalExt),
    {
      type: finalType,
      lastModified: Date.now(),
    }
  );
};

/**
 * 通用：从任意URL下载并转换为File对象 (支持 图片/视频/音频)
 * 自动处理跨域代理，并校验文件格式
 * @param {string} url - 资源地址
 * @param {string[]} formats - (可选) 允许的文件类型后缀数组，如 ['jpg', 'png', 'mp4']
 * @returns {Promise<File>}
 */
/**
 * 终极版：从URL下载并转换为File对象
 * 特点：取消盲目兜底、强化MIME识别、支持全媒体格式
 */
async function urlToFile(url, formats = []) {
  try {
    let fetchUrl = resolveFetchAssetUrl(url);
    const currentOrigin = window.location.origin;

    // 1. 跨域代理处理
    if (fetchUrl.startsWith("http") && !fetchUrl.includes(currentOrigin)) {
      fetchUrl = `/api/proxy?url=${encodeURIComponent(fetchUrl)}`;
    }

    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

    const blob = await response.blob();
    let fileExt = getExtensionFromUrl(url);
    let fileName = getFileNameFromUrl(url, fileExt);
    let finalBlob = blob;
    let finalType = blob.type;
    const hasOpaqueBinaryExtension = OPAQUE_BINARY_EXTENSIONS.has(fileExt);
    const detectedImageMime =
      hasOpaqueBinaryExtension || !finalType || isOpaqueBinaryMime(finalType)
        ? await detectImageMimeFromBlob(blob)
        : "";

    if (hasOpaqueBinaryExtension || detectedImageMime) {
      try {
        const normalizedImageFile = await convertBlobToImageFile(
          blob,
          fileName,
          hasOpaqueBinaryExtension
            ? "image/png"
            : getPreferredImageOutputType(formats),
          detectedImageMime
        );
        finalBlob = normalizedImageFile;
        finalType = normalizedImageFile.type;
        fileName = normalizedImageFile.name;
        fileExt = fileName.split(".").pop()?.toLowerCase() || "";
      } catch (error) {
        throw new Error("The provided URL did not return a valid image file.");
      }
    }

    if (!finalType || isOpaqueBinaryMime(finalType)) {
      if (detectedImageMime) {
        finalType = detectedImageMime;
      } else if (fileExt && URL_FILE_MIME_MAP[fileExt]) {
        finalType = URL_FILE_MIME_MAP[fileExt];
      }
    }

    // 4. 格式校验 (严格模式)
    if (formats.length > 0) {
      const lowerFormats = formats.map((f) => f.toLowerCase());
      // 校验依据：后缀匹配 OR MIME类型中包含关键字
      const isExtValid = lowerFormats.includes(fileExt);
      const isMimeValid = lowerFormats.some((fmt) =>
        finalType.toLowerCase().includes(fmt)
      );

      if (!isExtValid && !isMimeValid) {
        throw new Error(
          `[Format Blocked] Detected: ${finalType}, Allowed: ${formats.join(
            ","
          )}`
        );
      }
    }

    if (!fileName.includes(".") && MIME_EXTENSION_MAP[finalType]) {
      fileName = `${fileName}.${MIME_EXTENSION_MAP[finalType]}`;
    }

    // 6. 最终封装 - 绝不默认 audio/mpeg
    return new File([finalBlob], fileName, { type: finalType });
  } catch (error) {
    console.error("Critical: urlToFile failed ->", error.message);
    throw error;
  }
}

/** 模型切换 */
const onModelChange = (model) => {
  console.log("模型切换:", model);
  isEditTask.value = false;
};

/** 重新编辑任务 */
async function onReeditTask(task, isFirstModel = false) {
  if (!isFirstModel && !ensureTaskModelEnabled(task, "re-edit")) return;
  isEditTask.value = true;
  console.log("重新编辑任务:", task);
  setActive(task.id);
  if (isFirstModel && enabledModelVersions.value.length > 0) {
    task.model = enabledModelVersions.value[0].key;
  }
  const { formData, fields } = buildFormDataFromTask(task);
  const uploadFieldMap = getUploadFieldMapFromTask(task, task.model);

  try {
    isLoadingInputImages.value = true;
    await hydrateUploaderFields(formData, fields, uploadFieldMap);
    Object.assign(form, formData);
  } catch (error) {
    console.error("重新编辑任务失败:", error);
    toast.add({
      title: "Failed",
      description: error.message,
      color: "error",
      duration: Infinity, // 设置为无限大
      icon: "i-lucide-alert-triangle",
      progress: false,
      orientation: "horizontal",
      actions: [
        {
          icon: "i-lucide-copy",
          label: "Copy",
          color: "neutral",
          variant: "outline",
          onClick: (e) => {
            e?.stopPropagation();
            copyPrompt(error.message);
          },
        },
      ],
    });
  } finally {
    isLoadingInputImages.value = false;
  }
}

/**图生图重新生成图片（在图生图中重新生成）*/
async function onRefineInImg2Img(task) {
  const selectedOutputUrl = getTaskSelectedOutputUrl(task);
  const newTask = {
    ...task,
    uploadFieldMap: {
      input_image: stripR2UploadValue(selectedOutputUrl || task.outPutImageUrl),
    },
    prompt: "",
    toolName: "image-to-image",
    isFirstModel: true,
  };
  console.log("Re-edit task:", newTask);
  // 将任务数据和操作类型存储到 store
  store.commit("ai/SET_PENDING_TASK", {
    task: newTask,
    action: "re-edit",
  });
  // navigateTo(`/ai/image/image-to-image`);

  // 如果当前路由中没有image-to-image，则跳转到/ai/image/image-to-image，否则跳转到当前路由
  if (!window.location.pathname.includes("image-to-image")) {
    navigateTo(`/ai/image/image-to-image`);
  } else {
    await handlePendingTask();
  }
  // await handlePendingTask();
}

/** 下载任务 */
function onDownloadTask(task) {
  const urls = getTaskOutputUrls(task);
  if (!urls.length) return;

  // 如果是多图片，传递所有URL；否则只传递单个URL
  const imageurls = urls.length > 1 ? urls.join(",") : urls[0];
  const filename = urls[0].split("/").pop();
  const params = new URLSearchParams({
    img_path: imageurls,
    // prev_route1 = 用户实际所在的工具页(此前硬编码 /free-tools/image/<tool>,对 AI 音频/视频等页面是错的)。
    prev_route1: typeof window !== "undefined" ? window.location.pathname : "",
    prev_route1_title: toolTitle.value,
    img_title: filename,
  });
  window.open(`/download/?${params.toString()}`);
}

/** 图生视频*/
function onGenerateVideo(task) {
  console.log("生成视频", task);
  console.log("Generate video for task:", task);
  // TODO: 实现生成视频功能
  const selectedOutputUrl = getTaskSelectedOutputUrl(task);
  const newTask = {
    ...task,
    uploadFieldMap: {
      input_image: stripR2UploadValue(selectedOutputUrl || task.outPutImageUrl),
    },
    prompt: "",
    toolName: "image-to-video",
    isFirstModel: true,
  };
  console.log("Re-edit task:", newTask);
  // 将任务数据和操作类型存储到 store
  store.commit("ai/SET_PENDING_TASK", {
    task: newTask,
    action: "re-edit",
  });
  navigateTo(`/ai/video/image-to-video/`);
}

/** 为移动端生成下拉菜单项 */
function getTaskDropdownItems(task) {
  let items = [];

  if (canOperateTask(task)) {
    items.push({
      label: "Regenerate",
      icon: "material-symbols-light:star-shine-outline-rounded",
      disabled:
        isTaskPending(task) || isWorking.value || isLoadingInputImages.value,
      onSelect: () => onRegenerateTask(task),
    });
  }

  // 添加重新编辑图片选项
  if (isShowReEditImage && canOperateTask(task)) {
    items.push({
      label: "Re-edit",
      icon: "material-symbols-light:edit-road-rounded",
      disabled:
        isTaskPending(task) || isWorking.value || isLoadingInputImages.value,
      onSelect: () => onReeditTask(task),
    });
  }

  // 添加细化图片选项
  if (isShowRefineImage) {
    items.push({
      label: "Refine",
      icon: "material-symbols-light:photo-library-rounded",
      disabled:
        isTaskPending(task) || isWorking.value || isLoadingInputImages.value,
      onSelect: () => onRefineInImg2Img(task),
    });
  }

  return [items];
}

/** 归档任务 */
async function onArchiveTask(id) {
  try {
    await store.dispatch("ai/setUserHistory", {
      id: id,
      set_type: "archive",
    });
  } catch (error) {
    console.error("归档任务失败:", error);
    toast.add({
      title: "Failed archiving task",
      color: "error",
      duration: Infinity, // 设置为无限大
      icon: "i-lucide-alert-triangle",
    });
  }
}

/** 加载历史任务 */
async function loadUserTasks(
  networkRequest = false,
  targetToolName = toolName.value
) {
  isLoadingHistory.value = true;
  try {
    // 首先检查 store 中是否有缓存
    const cachedHistory = store.getters["ai/getHistoryByTool"](targetToolName);

    if (
      cachedHistory &&
      Array.isArray(cachedHistory) &&
      cachedHistory.length > 0 &&
      !networkRequest
    ) {
      // 如果有缓存，从缓存中过滤未归档的任务并获取最新的10个
      console.log("从缓存加载历史记录:", targetToolName);
      const unarchivedTasks = cachedHistory.filter((task) => !task.isArchived);
      const latestTasks = unarchivedTasks.slice(0, 10);
      if (targetToolName !== toolName.value) {
        return;
      }
      setTaskList(latestTasks);
      console.log(
        "缓存 (全部):",
        cachedHistory.length,
        "未归档:",
        unarchivedTasks.length,
        "显示:",
        latestTasks.length
      );

      // // toast success
      // toast.add({
      //   title: "Success",
      //   description: `Loaded ${latestTasks.length} tasks from cache`,
      //   color: "success",
      //   icon: "i-lucide-check-circle",
      // });
    } else {
      // 如果没有缓存，从接口请求全部数据
      console.log("从接口加载历史记录:", targetToolName);

      const res = await store.dispatch("ai/getUserHistory", {
        userId: userId.value,
        is_all: 0, // 获取全部历史记录
        tool_name: targetToolName,
      });

      const historyItems = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.items)
        ? res.data.items
        : [];
      if (res?.status === 200 && Array.isArray(historyItems)) {
        // 将API返回的数据转换为任务列表格式
        const userTasks = historyItems.map((item) => {
          const inputPayload = parseMaybeJson(item.input, {});
          const inputJson =
            inputPayload && !Array.isArray(inputPayload) ? inputPayload : {};
          const outputPayload = parseMaybeJson(item.output, item.output || []);
          const outputUrls = Array.isArray(outputPayload)
            ? outputPayload
            : normalizeUploadValue(outputPayload);
          const normalizedOutputUrls = resolveUserUploadValue(outputUrls);
          const outPutImageUrl = normalizedOutputUrls[0] || null;
          return buildTaskFromInputJson(item.model, inputJson, {
            id: item.id,
            model: item.model,
            status: item.status || "succeeded",
            isPublic: item.isPublic === "1",
            isArchived: item.isArchived === "1",
            outPutImageUrl: outPutImageUrl,
            outputUrls: normalizedOutputUrls,
            createdAt: new Date(
              /\d{2}:\d{2}:\d{2}$/.test(item.completed_at)
                ? item.completed_at + "Z"
                : item.completed_at
            ).getTime(),
            creditCost: item.creditCost,
            toolName: targetToolName,
          });
        });

        // 存储到 Vuex Store（存储全部任务，包括已归档的）
        store.commit("ai/SET_HISTORY_CACHE", {
          toolName: targetToolName,
          tasks: userTasks,
        });

        // 过滤掉已归档的任务，只显示未归档的最新10个
        const unarchivedTasks = userTasks.filter((task) => !task.isArchived);
        const latestTasks = unarchivedTasks.slice(0, 10);
        if (targetToolName !== toolName.value) {
          return;
        }
        setTaskList(latestTasks);
        console.log(
          "userTasks (全部):",
          userTasks.length,
          "未归档:",
          unarchivedTasks.length,
          "显示:",
          latestTasks.length
        );
      }
    }
  } catch (error) {
    console.error("加载用户历史任务失败:", error);
    toast.add({
      title: "Failed to load history",
      color: "error",
      duration: Infinity, // 设置为无限大
      icon: "i-lucide-alert-triangle",
    });
  } finally {
    if (targetToolName === toolName.value) {
      isLoadingHistory.value = false;
    }
  }
}

async function reloadPanelDataByTool() {
  const targetToolName = toolName.value;
  if (!targetToolName) return;

  setTaskList([]);
  cardTitleTab.value = token.value ? "tasks" : "effects";
  resetReactiveObject(archivePopoverOpen.value);
  resetReactiveObject(taskOutputActiveIndex);

  const loaders = [];
  if (token.value) {
    loaders.unshift(loadUserTasks(false, targetToolName));
  }
  await Promise.all(loaders);

  if (token.value) {
    cardTitleTab.value = hasDisplayableTasks.value ? "tasks" : "effects";
  }
}

watch(cardTitleTab, async (newVal) => {
  if (newVal === "tasks" && token.value && tasks.value.length === 0) {
    await loadUserTasks();
  }
});

watch(
  () => props.toolName,
  async (newToolName, oldToolName) => {
    if (!oldToolName || newToolName === oldToolName) return;
    await reloadPanelDataByTool();
  }
);

function getRandomStr(randomlength = 8) {
  /**
   * 生成带时间戳的随机字符串
   * @param {number} randomlength
   * @returns {string}
   */
  const time_start = Date.now() / 1000; // Python's time.time() returns seconds (float)
  let random_str = "";
  const base_str =
    "ABCDEFGHIGKLMNOPQRSTUVWXYZabcdefghigklmnopqrstuvwxyz0123456789";
  const length = base_str.length - 1;
  for (let i = 0; i < randomlength; i++) {
    random_str += base_str[Math.floor(Math.random() * (length + 1))];
  }
  random_str += String(Math.floor(time_start));
  return random_str;
}

const mimeToExtension = {
  // Audio
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/mp4": "m4a",
  "audio/x-m4a": "m4a",
  "audio/aac": "aac",
  "audio/ogg": "ogg",
  "audio/flac": "flac",
  "audio/x-flac": "flac",

  // Video
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/x-ms-wmv": "wmv",
  "video/webm": "webm",
  "video/x-matroska": "mkv",

  // Binary fallback
  "application/octet-stream": "bin",
};

const getUploadExtension = (fileBody) => {
  const contentType = (fileBody.type || "application/octet-stream").toLowerCase();
  let ext = mimeToExtension[contentType];

  if (!ext) {
    ext = contentType.split("/")[1]?.split("+")[0] || "bin";
  }

  if (ext === "mpeg") ext = "mp3";
  if (ext === "quicktime") ext = "mov";

  return ext;
};

const buildUploadKey = (task_id, fileBody) => {
  const user_id = userInfo.value?.user_info_id;
  const ext = getUploadExtension(fileBody);
  return `ai/tasks/${user_id}/${task_id}/upload-${getRandomStr()}.${ext}`;
};

const uploadToR2WithBinding = async ({ key, fileBody }) => {
  const formData = new FormData();
  formData.append("key", key);
  formData.append("file", fileBody, fileBody.name || key.split("/").pop());

  const response = await $fetch("/api/r2/user-upload", {
    method: "POST",
    body: formData,
  });

  return response?.key || key;
};

const uploadToR2WithPresignedUrl = async (config) => {
  const { task_id, fileBody, key: uploadKey } = config;
  try {
    const key = uploadKey || buildUploadKey(task_id, fileBody);
    // 获取预签名 URL
    const response = await $axios({
      method: "post",
      url: "sharkfoto/presign-url",
      data: {
        action: "upload", // 上传操作
        key, // 文件路径
        expires_in: 3600, // URL 有效期
      },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    const { url, key: presignedKey } = response.data;
    const resultKey = presignedKey || key;

    if (!url) {
      throw new Error("Failed to get presigned URL");
    }

    const formData = new FormData();
    formData.append("url", url);
    formData.append(
      "file",
      fileBody,
      fileBody.name || resultKey.split("/").pop()
    );

    // 通过同源服务端代理上传预签名 URL，避免浏览器直连 R2 触发 CORS。
    await $fetch("/api/r2/presigned-upload", {
      method: "POST",
      body: formData,
    });

    // 返回上传后的 R2 文件 URL
    // return `https://pub-a493cbae12d24ee498bfe20e3daa78f6.r2.dev/${resultKey}`;
    return resultKey;
  } catch (err) {
    console.error("R2 Upload failed", err);
    throw err;
  }
};

const uploadToR2 = async ({ task_id, fileBody }) => {
  const key = buildUploadKey(task_id, fileBody);

  try {
    return await uploadToR2WithBinding({ key, fileBody });
  } catch (error) {
    console.warn(
      "R2 binding upload unavailable, falling back to presigned URL",
      error
    );
    return uploadToR2WithPresignedUrl({ task_id, fileBody, key });
  }
};

// 批量上传并返回 S3 地址数组，失败单个文件最多重试3次
async function filesListToS3(task_id, filesList) {
  // 单个文件上传重试3次
  async function uploadWithRetry(task_id, fileBody, maxAttempts = 3) {
    let attempt = 0;
    while (attempt < maxAttempts) {
      try {
        return await uploadToR2({ task_id, fileBody });
      } catch (err) {
        attempt++;
        if (attempt >= maxAttempts) {
          toast.add({
            title: "R2 Upload fail, please try again!",
            color: "error",
            duration: Infinity, // 设置为无限大
            icon: "i-lucide-alert-triangle",
          });
          throw err;
        }
      }
    }
  }

  // 所有 Promise 并发执行，并等待全部完成（结果为地址数组）
  const uploadPromises = filesList.map((fileBody) =>
    uploadWithRetry(task_id, fileBody)
  );
  return Promise.all(uploadPromises);
}

// 从父组件注入 scrollToTop 函数
const scrollToTop = inject("scrollToTop", () => {
  console.warn("scrollToTop function not provided");
});
</script>

<style scoped>
.text-muted {
  color: var(--ui-text-muted);
}
/* 过渡组：进入/离开动画 */
.task-fade-enter-from,
.task-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
.task-fade-enter-active,
.task-fade-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}
.task-fade-move {
  transition: transform 180ms ease;
}
.task-fade-leave-active {
  position: absolute;
  left: 0;
  right: 0;
}

#tasks-card {
  overflow-x: hidden;
}

/* 任务卡片折叠动画 */
.task-collapse-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.task-collapse-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.task-collapse-enter-from {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}

.task-collapse-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}

.task-collapse-enter-to,
.task-collapse-leave-from {
  opacity: 1;
  max-height: 5000px;
  transform: translateY(0);
}

.merge-bgm-bar {
  width: 2px;
  height: 100%;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 1px;
  animation: merge-bgm-bar-bounce 0.8s ease-in-out infinite;
  transform-origin: bottom;
}

.merge-bgm-bar--delay-1 {
  animation-delay: 0.15s;
}

.merge-bgm-bar--delay-2 {
  animation-delay: 0.3s;
}

.merge-bgm-bar--delay-3 {
  animation-delay: 0.45s;
}

@keyframes merge-bgm-bar-bounce {
  0%,
  100% {
    transform: scaleY(0.35);
  }
  50% {
    transform: scaleY(1);
  }
}

/* 2. 定义平滑展开/收起的动画 */
.expand-enter-active,
.expand-leave-active {
  /* 使用 ease-in-out 会更自然 */
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  /* 关键 1：设置为实际宽度 + margin 的近似值，不要设太大 */
  max-width: 60px;
  overflow: hidden;
  white-space: nowrap;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-width: 0;
  /* 关键 2：必须强制清除 margin，否则右侧元素无法完全靠左 */
  margin-right: 0 !important;
  /* 关键 3：微调位移，增加“抽屉”拉出的视觉感 */
  transform: translateX(-10px);
}
</style>
